// The data layer. Screens never write to tables. Reads select from tables and views, always filtered on tenant_id
// (row-level security also enforces it). Writes go through the "act" Edge Function (lib/act.ts, docs/SECURITY.md):
// signed-in users may only read, so act checks the user and calls the crm_* function as the service role, with the
// business and the actor ("human:<their sign-in email>", which the gates 0.5, 1.21, 4.10, 6.11, 9.6 require) set on
// the server. The one direct write is crm_store_secret, which checks the business itself.

import type { SectorKey } from "./sectors";
import type { CampaignStyle, SendingStyle } from "./sendingStyle";
import { readHappinessMonths, type HappinessMonth } from "./happiness";
import type { SupabaseClient } from "@supabase/supabase-js";
import { readResetPreview, readResetResult, type ResetPreview, type ResetResult } from "./startAgain";
import { makeAct, type ActFn } from "./act";
import { resultFrom, type AskColumn, type AskQueryResponse, type AskResult, type AskSpec } from "./ask";
import { shapePreview, type ActionKind, type ActionParams, type ActionPreview, type BulkResult, type Campaign, type CampaignCreated, type PreviewResponse } from "./askActions";
import type { SignoffStatus, StepDetail, StepLive, StepSignoff } from "./process";
import type { MetricResult, MetricSpec, Performance } from "./performance";
import type { FindResults, WeekRow, WeekSummary } from "./findResults";
import type { SendTimeResults } from "./sendTimes";
import type { MailboxHealth } from "./mailboxHealth";
import type { FirmFacts, GroupedList, ProspectResult, ProspectSearch, RankCriterion, RankedGroup, RankedList, RefFirm, RefNiche, RefSectorCount,
              SearchProgress } from "./prospects";
import { factArgs, withSectors } from "./prospects";
import type { SearchHealth, WidenKind } from "./searchHealth";
import type { FileContactRow, ImportRow } from "./knownContacts";
import { compact, type AddContactsResult, type AddResult, type ContactEdit, type ContactInput, type FirmEdit, type FirmInput, type LatestInput } from "./contactsByHand";
import type { HiringRow, MoveDecision, MoveResult, MoveRow, PlayRole } from "./signals";
import {
  filterParams,
  readHistoryStatus,
  uploadProblem,
  type HistoryOptions,
  type HistoryRow,
  type HistoryStatus,
  type PlanResult,
  type SpokenFilters,
  type SpokenRow,
  type Warmth,
} from "./history";

/** The private bucket for WhatsApp exports (migration 20261117100000): members may add files, never read them back. */
export const HISTORY_BUCKET = "history-uploads";

/** crm_relationship_history: who, the summary, and the past conversations newest first (never full text). */
export interface RelationshipHistory {
  person: { id: string; full_name: string | null; role: string | null; email: string | null; organisation_id: string | null } | null;
  organisation: { id: string; name: string } | null;
  summary: {
    warmth: Warmth;
    last_topic: string | null;
    how_it_ended: string | null;
    suggested_follow_up: string | null;
    open_promises: string[];
    last_contact_at: string | null;
    interactions_count: number;
    sources: string[];
  } | null;
  items: { at: string; direction: "in" | "out"; channel: string; kind: string | null; source: string | null; subject: string | null; summary: string | null; person: string | null }[];
}

import type { Coverage, LookupRow } from "./targeting";

/** 2.33 crm_cadence_inputs. */
export interface CadenceInputs {
  first_emails: number;
  replied: number;
  meetings: number;
  mailboxes: { status: string; age_days: number; daily_cap: number | null; provider?: string | null }[];
}
import type { BusinessRecord, Listing, NewReview, ReferralAskRow, ReviewRequestRow, ReviewRow } from "./listings";
import type { CtaRow } from "./ctas";
import type { CampaignRunRequests, CampaignRunStep, HandSend, HandSendChannel } from "./types";
import type { BookingOverview } from "./booking";
import type { SignupConfig, SignupResult, WhatsAppAccount, WhatsAppTemplate } from "./whatsapp";
import { readMetaAccounts, readMetaAccountsView, type MetaAccount, type MetaConnectResult, type MetaLoginConfig } from "./meta";
import type { UpdateDraft, UpdatePreview, UpdateSettings, WhatsAppSpend, WhatsAppUpdate } from "./whatsappUpdates";
import type { WaInbox, WaThread } from "./whatsappInbox";
import type { CallList, CallOutcome, OutcomeResult, PhoneCheckSource, StartedCall } from "./calls";
import type { ConnectOffer, Flavour, Playbook, ReconnectStatus } from "./connect";
import type { BlockKey, CampaignSummary, CompleteBlockResult, ConnectRun, PathOutcome, PathsBoard, StartPathResult } from "./paths";
import type { Answers, AssistantReviewRow, AssistantTry, KnowledgeEntry, PriceBook, PriceBookRow, PriceDefinition, QuoteQuestion, QuoteRow } from "./quotes";
import type { FirmQuoteResponse, FlowDefinition, FlowPurpose, QuoteFlowRow, QuoteResponseRead } from "./questionFlows";
import type { AudiencesRead, RoutePiece, RoutesBoard, StageKey } from "./routes";
import type { ModulesRead } from "./modules";
import type { OwnerStatus } from "./owners";
import type { ContentExperiment, EvergreenRow, SegmentRow } from "./library";
import type { AdsView } from "./ads";
import type { AdAudience, IdentitySheet, PresenceView, RetargetReadiness, VideoStudio } from "./presence";
import type { EventChoice, EventDetail, EventResult, EventRow, IndustryCalendar, NewContact, NewEvent } from "./events";
import type { SearchReports, SyndicationPack } from "./syndication";
import type { BufferChannel, SettingsSection } from "./settingsForms";
import type { OnboardingProgress } from "./onboarding";
import type { CadenceSettings, EnginePart, EngineSchedule } from "./engineSchedule";
import { RULES_SWITCHES } from "./engineHubs";
import type { DnsCheck, DnsHost, DomainVerification, PageCheck, SendingDomain, SetupInfo, SiteRow } from "./domainSetup";
import { readPlay, readSetupState, type CoreFields, type Play, type PlayAudience, type RouteChoice, type Sample, type SetupView } from "./setupFlow";
import type { ModuleKey, ModuleState, PriorityKey } from "./setupCatalogue";
import { readSpeedToLead, type SpeedToLead } from "./speedToLead";
import { readOwnerMinutes, type OwnerMinutes, type TimeEntry } from "./ownerTime";
import { BRAND_FOLDER, type BrandAsset, type BrandState, type SiteBrief, type SiteState, type SiteVersion } from "./studio";
import type {
  AnswerIntent,
  AttendanceOutcome,
  ChecklistItem,
  DealOutcome,
  MeetingDetail,
  ConfigVersion,
  ContactSource,
  ContactTag,
  FirmContext,
  LinkKind,
  ContentAsset,
  ContentItem,
  Deal,
  DealDocument,
  DocumentKind,
  Enrolment,
  FirstLoginState,
  HumanTask,
  JsonObject,
  Meeting,
  Membership,
  Message,
  Organisation,
  PlaybookFeedbackInput,
  PlaybookFeedbackRow,
  OutboxItem,
  Overview,
  ReviewDraft,
  OutreachAssetPreview,
  Person,
  PersonContext,
  PersonNetwork,
  PersonSearch,
  ModelSpend,
  AutonomyKind,
  AutoApproved,
  BudgetStatus,
  RejectReason,
  ReplyIntent,
  Signal,
  Stage,
  TenantDocument,
  TenantRow,
  TimelineEntry,
  VendorStatus,
} from "./types";

export const DOCUMENTS_BUCKET = "tenant-documents";

export interface KnownContactsSummary {
  total: number;
  by_kind: Record<string, number>;
  by_source: Record<string, number>;
  last_seen: string | null;
  customers: number;
}
const CONTENT_COLUMNS = "id, kind, title, body, status, network, account, scheduled_at, published_url, review_note, parent_id, assets, meta, audience, route";

/** The actor string for a person. Used for crm_store_secret only; act sets the actor for every other write. */
export function humanActor(email: string | null | undefined): string {
  const e = (email ?? "").trim().toLowerCase();
  if (!e) throw new Error("Your account has no email address, so changes cannot be signed.");
  return `human:${e}`;
}

export class CrmError extends Error {
  constructor(
    message: string,
    readonly detail?: string,
  ) {
    super(message);
    this.name = "CrmError";
  }
}

interface PgError {
  message: string;
  details?: string | null;
  hint?: string | null;
  code?: string;
}

/** Turns a database error into a sentence a person can act on. Raised messages from crm_* functions are already plain English. */
export function friendlyError(err: PgError | Error | unknown): CrmError {
  if (err instanceof CrmError) return err;
  const msg = (err as PgError)?.message ?? String(err);
  if (/JWT|not authenticated|401/i.test(msg)) return new CrmError("Your session has ended. Sign in again.", msg);
  if (/Failed to fetch|NetworkError|network/i.test(msg)) return new CrmError("Can't reach the server. Check your connection and try again.", msg);
  if (/permission denied|row-level security/i.test(msg)) return new CrmError("You don't have access to that.", msg);
  const first = msg.charAt(0).toUpperCase() + msg.slice(1);
  return new CrmError(first.endsWith(".") ? first : `${first}.`, msg);
}

/** Tag keys as the database accepts them (migration 20261011000014): lower case letters, digits, - or _, up to 40. */
export const TAG_KEY_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;
export const MAX_PEOPLE_PER_TAGGING = 1000;
export const STEP_TAGS = "3.10";

/** A tag key from a label: "Corporate finance" -> "corporate-finance". Empty when nothing usable is left. */
export function tagKeyFrom(label: string): string {
  return label
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^[-_]+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");
}

function cleanTags(tags: string[]): string[] {
  const out = [...new Set(tags.map((t) => t.trim().toLowerCase()).filter(Boolean))];
  const bad = out.find((t) => !TAG_KEY_RE.test(t));
  if (bad) throw new CrmError(`"${bad}" isn't a tag key. Use lower case letters, numbers, - or _.`);
  return out;
}

export const STEP_CONNECTIONS = "3.11";
export const LINK_KINDS = ["introduced", "referred", "advises", "works_with", "knows", "reports_to", "family", "invests_in", "lends_to"] as const;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface SourceChange {
  person: string;
  source?: string;
  source_detail?: string;
  introduced_by?: string;
  connected_on?: string;
  clear_introducer?: boolean;
}

export interface LinkChange {
  from: string;
  kind: string;
  to_person?: string;
  to_organisation?: string;
  note?: string;
  since?: string;
}

export interface PeopleQuery {
  query?: string;
  tags?: string[];
  organisation?: string | null;
  limit?: number;
  offset?: number;
  /** A source key (crm_contact_sources) or text as typed. */
  source?: string | null;
  /** Only people this person introduced. */
  introducedBy?: string | null;
  /** First-touch channel key of inbound leads (crm_inbound_channels), or "any" for every inbound lead. */
  channel?: string | null;
  /** Only people in this module now (lib/modules.ts). */
  module?: string | null;
}

/**
 * An action applies to exactly the rows the Ask list holds: its source, filters, sort and limit (and its columns,
 * whose first one is the default order). The database works the rows out again; it never takes a list of ids.
 */
/** 0.45 (#377) What a phone number is (migration 20270123090000). Act sets it after the add or edit; null clears it. */
export interface ContactExtras {
  phone_label?: "head_office" | "branch" | "mobile" | "other" | null;
}
/** 0.45 (#376) Every country a firm works in, main one first ([] clears them); given instead of country. */
export interface FirmExtras extends ContactExtras {
  countries?: string[];
}

export function actionSpec(spec: AskSpec): Pick<AskSpec, "filters" | "sort" | "limit" | "columns"> & { source: string } {
  const out: Pick<AskSpec, "filters" | "sort" | "limit" | "columns"> & { source: string } = { source: String(spec.source), filters: spec.filters ?? [] };
  if (spec.sort?.length) out.sort = spec.sort;
  if (typeof spec.limit === "number") out.limit = spec.limit;
  if (spec.columns?.length) out.columns = spec.columns;
  return out;
}

// Lead stage changes made from the board, and deal moves, are logged against these step ids.
export const STEP_STAGE_CHANGE = "6.W";
export const STEP_DEAL_CHANGE = "9.W";


export interface EngineSwitch {
  key: string;
  paused: boolean;
  reason: string | null;
  changed_by: string;
  changed_at: string;
}

export class Crm {
  private readonly actFn: ActFn;

  constructor(
    private readonly sb: SupabaseClient,
    readonly tenantId: string,
    readonly email: string,
    readonly userId: string,
    act?: ActFn,
  ) {
    this.actFn = act ?? makeAct(sb);
  }

  // ---------------------------------------------------------------- plumbing
  /** Read functions only: signed-in users may not call write functions directly. */
  private async rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
    const { data, error } = await this.sb.rpc(fn, args);
    if (error) throw friendlyError(error);
    return data as T;
  }

  /** Every write: the act function, which forces this person's business and actor on the server. */
  private async act<T>(action: string, params: Record<string, unknown>): Promise<T> {
    try {
      return (await this.actFn(action, params)) as T;
    } catch (e) {
      throw friendlyError(e);
    }
  }

  private async rows<T>(query: PromiseLike<{ data: unknown; error: PgError | null }>): Promise<T[]> {
    const { data, error } = await query;
    if (error) throw friendlyError(error);
    return (data ?? []) as T[];
  }

  private async one<T>(query: PromiseLike<{ data: unknown; error: PgError | null }>): Promise<T | null> {
    const { data, error } = await query;
    if (error) throw friendlyError(error);
    return (data ?? null) as T | null;
  }

  private t(table: string) {
    return this.sb.from(table);
  }

  /**
   * A read function for this business: `p_tenant` is added. For feature libs (lib/<feature>.ts); never add methods
   * here. Upgrades Oct 2026 (docs/specs/UPGRADES_2026_10.md).
   */
  read<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
    return this.rpc<T>(fn, { ...args, p_tenant: this.tenantId });
  }

  /**
   * A write through the act function, which forces this person's business and actor on the server. For feature libs
   * (lib/<feature>.ts); never add methods here. Upgrades Oct 2026 (docs/specs/UPGRADES_2026_10.md).
   */
  write<T>(action: string, params: Record<string, unknown> = {}): Promise<T> {
    return this.act<T>(action, params);
  }

  // ---------------------------------------------------------------- 0.R onboarding
  firstLoginState() {
    return this.rpc<FirstLoginState>("crm_first_login_state", { p_tenant: this.tenantId });
  }

  checklist() {
    return this.rpc<ChecklistItem[]>("crm_onboarding_checklist", { p_tenant: this.tenantId });
  }

  storeSecret(vendor: string, name: string, value: string) {
    if (!value.trim()) throw new CrmError("Paste a value before saving.");
    // Direct rpc: crm_store_secret is security definer and refuses any business but the caller's own.
    return this.rpc<void>("crm_store_secret", {
      p_tenant: this.tenantId,
      p_actor: humanActor(this.email),
      p_vendor: vendor,
      p_name: name,
      p_value: value.trim(),
    });
  }

  setVendorStatus(vendor: string, status: VendorStatus, notes?: string) {
    return this.act<void>("set_vendor_status", { vendor, status, ...(notes ? { notes } : {}) });
  }

  /** Step 0.1 (set on the server). A file must already be uploaded under <tenant_id>/ (uploadDocument). */
  registerDocument(doc: { kind: DocumentKind; title: string; step?: string; storagePath?: string; body?: string; metadata?: JsonObject }) {
    return this.act<string>("register_document", {
      kind: doc.kind,
      title: doc.title,
      ...(doc.storagePath ? { storage_path: doc.storagePath } : {}),
      ...(doc.body !== undefined ? { body: doc.body } : {}),
      metadata: doc.metadata ?? {},
    });
  }

  /** Uploads a file to <tenant_id>/<folder>/<timestamp>-<name> in the private documents bucket and returns the path. */
  async uploadDocument(file: File, folder: string): Promise<string> {
    const safe = file.name.replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "file";
    const path = `${this.tenantId}/${folder}/${Date.now()}-${safe}`;
    const { error } = await this.sb.storage.from(DOCUMENTS_BUCKET).upload(path, file, { upsert: false, contentType: file.type || undefined });
    if (error) throw friendlyError(error);
    return path;
  }

  documents() {
    return this.rows<TenantDocument>(
      this.t("tenant_documents").select("id, kind, title, storage_path, created_at, metadata").eq("tenant_id", this.tenantId).order("created_at", { ascending: false }),
    );
  }

  finishOnboarding() {
    return this.act<void>("finish_onboarding", {});
  }

  /** 0.R The first-login intro has been seen or skipped, so it does not play again (tenant_members.intro_seen_at). */
  markIntroSeen() {
    return this.act<string | null>("mark_intro_seen", {});
  }

  /** Step 0.8: a ten-minute signed link to Google's consent screen for this mailbox (oauth-google /start). */
  async googleConnectUrl(address: string): Promise<string> {
    const r = await this.act<{ url?: string }>("google_connect_url", { address: address.trim().toLowerCase() });
    if (!r?.url) throw new CrmError("The mailbox link couldn't be made. Try again in a moment.");
    return r.url;
  }

  /** Step 0.8: the same for an Outlook / Microsoft 365 mailbox (oauth-microsoft /start). docs/MICROSOFT_365.md */
  async microsoftConnectUrl(address: string): Promise<string> {
    const r = await this.act<{ url?: string }>("microsoft_connect_url", { address: address.trim().toLowerCase() });
    if (!r?.url) throw new CrmError("The mailbox link couldn't be made. Try again in a moment.");
    return r.url;
  }

  // ---------------------------------------------------------------- 0.5 settings
  configVersions() {
    return this.rows<ConfigVersion>(
      this.t("tenant_config_versions").select("*").eq("tenant_id", this.tenantId).order("version", { ascending: false }),
    );
  }

  decideConfig(version: number, approve: boolean, reason?: string) {
    return this.act<void>("decide_config", { version, approve, ...(reason ? { reason } : {}) });
  }

  // ---------------------------------------------------------------- 0.W, 0.12, 0.R settings in the product (migration 20261110140000)
  /** One section's changed fields as a new settings version for the owner to approve (decideConfig). */
  proposeSettingsChange(section: SettingsSection, value: JsonObject, note?: string) {
    return this.act<{ version: number; section: string; fields: string[] }>("propose_settings_change", { section, value, ...(note?.trim() ? { note: note.trim() } : {}) });
  }

  /** 0.12 The channels on the business's Buffer account (the key stays on the server). */
  async bufferChannels(): Promise<BufferChannel[]> {
    const r = await this.act<{ channels?: BufferChannel[] }>("buffer_channels", {});
    return r?.channels ?? [];
  }

  /** 0.R The set-up steps marked done or left for later, and the waiting settings change. */
  onboardingProgress() {
    return this.rpc<OnboardingProgress>("crm_onboarding_progress", { p_tenant: this.tenantId });
  }

  markOnboardingStep(step: string, status: "skipped" | "done" | "open") {
    return this.act<{ step: string; status: string }>("mark_onboarding_step", { step, status });
  }

  // ---------------------------------------------------------------- 0.35-0.39 guided set-up (docs/specs/GUIDED_SETUP_SCREENS.md)
  /** Where set-up stands: the business core, the route, each module and part, the plays and where to resume. */
  async setupState(): Promise<SetupView> {
    return readSetupState(await this.rpc<unknown>("crm_setup_state", { p_tenant: this.tenantId }));
  }

  /** 0.36 Save the business core; confirm=true is the owner's confirmation (human only on the server). */
  /** 0.36 The owner's sector: kept with the core; each setting still at a default takes the sector's value (force: all). */
  applySectorDefaults(sector: SectorKey, force = false) {
    return this.act<number>("apply_sector_defaults", { sector, ...(force ? { force } : {}) });
  }

  saveCore(core: Partial<CoreFields>, confirm = false) {
    return this.act<number>("save_core", { core, confirm });
  }

  /** 0.37 The route: the owner's goal (or the suggestion they accepted) and the module to start with. */
  setRoute(route: Omit<RouteChoice, "source"> & { source?: RouteChoice["source"] }) {
    return this.act<number>("set_route", { route: { source: "owner", ...route } });
  }

  /** 0.38 Switch a module on, off or to later; a module off or later pauses its parts of the engine. */
  setModuleState(module: ModuleKey, state: ModuleState, reason?: string) {
    return this.act<unknown>("set_module_state", { module, state, ...(reason?.trim() ? { reason: reason.trim() } : {}) });
  }

  /** 0.37 (set-up v2) The owner's priority, in one call: the route, its first module on, modules nobody chose back to
   *  later, Connect on. Returns the new set-up state, so the screen needs no second read. */
  async choosePriority(priority: PriorityKey): Promise<SetupView> {
    return readSetupState(await this.act<unknown>("choose_priority", { priority }));
  }

  /** 0.38 (set-up v2) Several modules on, off or later in one call. Returns the new set-up state. */
  async setModules(states: Partial<Record<ModuleKey, ModuleState>>, reasons?: Partial<Record<ModuleKey, string>>): Promise<SetupView> {
    return readSetupState(await this.act<unknown>("set_modules", { states, ...(reasons && Object.keys(reasons).length ? { reasons } : {}) }));
  }

  /** 2.30 (set-up v2) The first module's play, drafted from the business core, with its offer workshop started. */
  async startFirstPlay(): Promise<{ play: Play | null; created: boolean; state: SetupView }> {
    const r = (await this.act<Record<string, unknown> | null>("start_first_play", {})) ?? {};
    return { play: readPlay(r.play), created: r.created === true, state: readSetupState(r.state) };
  }

  /** 0.29 Which other CRM to load contacts from (tools.crm_import). */
  setCrmImport(provider: "hubspot" | "pipedrive" | "none") {
    return this.act<{ provider: string; changed: boolean }>("set_crm_import", { provider });
  }

  /** 0.38 Switch one part of a module on or off. */
  setModulePart(module: ModuleKey, part: string, on: boolean) {
    return this.act<unknown>("set_module_part", { module, part, on });
  }

  async plays(module?: ModuleKey): Promise<Play[]> {
    const rows = await this.rpc<unknown[] | null>("crm_plays", { p_tenant: this.tenantId, p_module: module ?? null });
    return (rows ?? []).map(readPlay).filter((x): x is Play => Boolean(x));
  }

  createPlay(module: ModuleKey, name: string, audience: Partial<PlayAudience>) {
    return this.act<string>("create_play", { module, name, audience });
  }

  updatePlay(play: string, patch: JsonObject) {
    return this.act<unknown>("update_play", { play, patch });
  }

  proposePlay(play: string) {
    return this.act<unknown>("propose_play", { play });
  }

  /** 0.39 Approve a play, or send it back with a note (human only). Approving a Find play starts the first-value job (2.34). */
  decidePlay(play: string, approve: boolean, note?: string) {
    return this.act<unknown>("decide_play", { play, approve, ...(note?.trim() ? { note: note.trim() } : {}) });
  }

  /** 2.34 The first-value samples for a play (a sample email, never sent). */
  async samples(play: string): Promise<Sample[]> {
    return ((await this.rpc<Sample[] | null>("crm_samples", { p_tenant: this.tenantId, p_play: play })) ?? []).filter(Boolean);
  }

  /** 2.34 Run the first-value job again for an approved Find play (when it failed or nothing arrived). */
  retryFirstValue(play: string) {
    return this.act<unknown>("retry_first_value", { play });
  }

  /** 2.34 Where a play's first-value job is: waiting, running, done or failed (with what went wrong). */
  async firstValueStatus(play: string): Promise<FirstValueStatus> {
    return readFirstValueStatus(await this.rpc<unknown>("crm_first_value_status", { p_tenant: this.tenantId, p_play: play }));
  }

  /** 0.39 Retire a play (human only). Refused for the only approved play of a module that is on. */
  retirePlay(play: string, note?: string) {
    return this.act<unknown>("retire_play", { play, ...(note?.trim() ? { note: note.trim() } : {}) });
  }

  /** The business's own requests to Ogma's team: open ones and those finished in the last 30 days. */
  async helpRequests(): Promise<HelpRequest[]> {
    const rows = await this.rpc<unknown[] | null>("crm_help_requests", { p_tenant: this.tenantId });
    return (Array.isArray(rows) ? rows : []).map(readHelpRequest).filter((x): x is HelpRequest => Boolean(x));
  }

  /** Anything the owner can't do themselves becomes a request to Ogma's team ("We're on it"). */
  requestHelp(topic: string, detail: string) {
    // The database keeps a short key per topic (one open request each): "Setting up email outreach" -> setting_up_email_outreach.
    return this.act<{ task_id: string }>("request_help", { topic: helpTopicKey(topic), ...(detail.trim() ? { detail: detail.trim() } : {}) });
  }

  /** Rejects the draft with the person's comments and asks the strategy agent for a revised version (one call). */
  async requestPlaybookChanges(version: number, comments: string) {
    if (!comments.trim()) throw new CrmError("Say what should change, so the next draft gets it right.");
    await this.act<string>("revise_config", { version, comments: comments.trim() });
  }

  // ---------------------------------------------------------------- 0.5 playbook review (migration 20261012100000)
  /** Every reaction recorded on this business's playbook drafts, newest version first. */
  playbookFeedback() {
    return this.rows<PlaybookFeedbackRow>(
      this.t("playbook_feedback").select("*").eq("tenant_id", this.tenantId).order("version", { ascending: false }),
    );
  }

  /** One reaction to a draft: a section verdict, a firm yes/no or an automation switch, with an optional note. */
  givePlaybookFeedback(f: PlaybookFeedbackInput) {
    if (f.verdict == null && !f.note?.trim()) throw new CrmError("Pick an answer or add a note.");
    return this.act<string | null>("playbook_feedback", {
      version: f.version,
      kind: f.kind,
      section: f.section,
      ...(f.item ? { item: f.item } : {}),
      ...(f.verdict ? { verdict: f.verdict } : {}),
      ...(f.note?.trim() ? { note: f.note.trim() } : {}),
      ...(f.via ? { via: f.via } : {}),
    });
  }

  /** The strategy agent's open revise_playbook task, if the owner has asked for changes it hasn't drafted yet. */
  async pendingPlaybookRevision() {
    const rows = await this.rows<{ id: string; payload: JsonObject; created_at: string }>(
      this.t("tasks").select("id, payload, created_at").eq("tenant_id", this.tenantId).eq("kind", "revise_playbook").in("status", ["open", "claimed"]).order("created_at", { ascending: false }).limit(1),
    );
    return rows[0] ?? null;
  }

  /** Takes back one note (by its position in that row's notes). */
  removePlaybookNote(f: { version: number; kind: PlaybookFeedbackInput["kind"]; section: string; item?: string; index: number }) {
    return this.act<void>("remove_playbook_note", { version: f.version, kind: f.kind, section: f.section, ...(f.item ? { item: f.item } : {}), index: f.index });
  }

  /** Sends everything said about a draft to the strategy agent, which drafts the next version. */
  sendPlaybookFeedback(version: number, comments?: string) {
    return this.act<string>("send_playbook_feedback", { version, ...(comments?.trim() ? { comments: comments.trim() } : {}) });
  }

  /** Example firms for the playbook's "would you want these?" section: the best-scoring targets, newest scores first. */
  async playbookFirms(limit = 5, skipStages: string[] = []) {
    const rows = await this.rows<Pick<Organisation, "id" | "name" | "city" | "segment" | "stage" | "fit_score" | "tier" | "attributes">>(
      this.t("organisations")
        .select("id, name, city, segment, stage, fit_score, tier, attributes")
        .eq("tenant_id", this.tenantId)
        .eq("kind", "target")
        .order("fit_score", { ascending: false })
        .limit(40),
    );
    return rows.filter((r) => !skipStages.includes(r.stage)).slice(0, limit);
  }

  // ---------------------------------------------------------------- tasks
  openHumanTasks() {
    return this.rows<HumanTask>(
      // Requests to Ogma's team (ogma_help) are not the owner's to-do: they are listed under Settings › Modules.
      this.t("v_open_human_tasks").select("*").eq("tenant_id", this.tenantId).neq("kind", "ogma_help").order("priority").order("due_at"),
    );
  }

  /** Only tasks of this business assigned to a person (the act function checks). */
  completeTask(task: string, status: "done" | "cancelled", result: JsonObject = {}) {
    return this.act<void>("complete_task", { task, status, result });
  }

  // ---------------------------------------------------------------- 1.21 content
  contentItem(id: string) {
    return this.one<ContentItem>(this.t("content_items").select("*").eq("tenant_id", this.tenantId).eq("id", id).maybeSingle());
  }

  /** 1.19 Who a piece is for: one of the business's audiences, or "general" on purpose. Not once it is published. */
  setContentAudience(item: string, audience: string) {
    const a = audience.trim().toLowerCase();
    if (!a) throw new CrmError("Choose who the piece is for.");
    return this.act<{ content_id: string; audience: string; route: string | null; changed: boolean }>("set_content_audience", { item, audience: a });
  }

  decideContent(item: string, approve: boolean, note?: string, editedBody?: string) {
    return this.act<void>("decide_content", { item, approve, ...(note ? { note } : {}), ...(editedBody !== undefined ? { body: editedBody } : {}) });
  }

  /** 1.2-1.8 Recent articles with their fact-check, sources and structure. */
  articles(limit = 6) {
    return this.rows<ContentItem>(
      this.t("content_items")
        .select(`${CONTENT_COLUMNS}, sources, fact_check, created_at`)
        .eq("tenant_id", this.tenantId)
        .in("kind", ["pillar", "blog"])
        .order("created_at", { ascending: false })
        .limit(limit),
    );
  }

  /** 1.4 The posts cut from one article. */
  postsFrom(parentId: string) {
    return this.rows<ContentItem>(
      this.t("content_items").select(CONTENT_COLUMNS).eq("tenant_id", this.tenantId).eq("parent_id", parentId).order("scheduled_at"),
    );
  }

  /** A one-hour link to a file in the business's own document storage (its logo, say); null for anything outside it. */
  async brandFileUrl(path: string): Promise<string | null> {
    if (!path.startsWith(`${this.tenantId}/`) || path.includes("..")) return null;
    const { data, error } = await this.sb.storage.from(DOCUMENTS_BUCKET).createSignedUrl(path, 3600);
    return error ? null : (data?.signedUrl ?? null);
  }

  /** 1.5 A link to show a visual: its SVG inline when stored, else a one-hour signed link to the file. */
  async assetUrl(asset: ContentAsset): Promise<string | null> {
    if (asset.svg) return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(asset.svg)}`;
    if (!asset.storage_path || !asset.storage_path.startsWith(`${this.tenantId}/`)) return null;
    const { data, error } = await this.sb.storage.from(DOCUMENTS_BUCKET).createSignedUrl(asset.storage_path, 3600);
    return error ? null : (data?.signedUrl ?? null);
  }

  contentBetween(fromIso: string, toIso: string) {
    return this.rows<ContentItem>(
      this.t("content_items")
        .select(CONTENT_COLUMNS)
        .eq("tenant_id", this.tenantId)
        .gte("scheduled_at", fromIso)
        .lt("scheduled_at", toIso)
        .order("scheduled_at"),
    );
  }

  unscheduledContent() {
    return this.rows<ContentItem>(
      this.t("content_items")
        .select(CONTENT_COLUMNS)
        .eq("tenant_id", this.tenantId)
        .is("scheduled_at", null)
        .in("status", ["draft", "checked", "awaiting_approval", "approved"])
        .neq("kind", "insight")
        .order("created_at", { ascending: false })
        .limit(50),
    );
  }

  // ---------------------------------------------------------------- 4.10 outbox
  batchDrafts(batchId: string) {
    return this.rows<OutboxItem>(
      this.t("outbox")
        .select("id, channel, step_id, subject, body, status, batch_id, scheduled_for, sent_at, created_at, person:people(full_name, email), organisation:organisations(id, name)")
        .eq("tenant_id", this.tenantId)
        .eq("batch_id", batchId)
        .eq("status", "awaiting_approval")
        .order("created_at"),
    );
  }

  /**
   * The batch as the reviewer sees it (crm_review_batch, migration 20261011000019): recipient, the personal hook and its
   * source, the country rule for the channel, and for a reply the message it answers. Until that read is deployed, the
   * plain drafts.
   */
  async reviewBatch(batchId: string): Promise<ReviewDraft[]> {
    const { data, error } = await this.sb.rpc("crm_review_batch", { p_tenant: this.tenantId, p_batch: batchId });
    if (error && !/Could not find|isn't available|does not exist|PGRST202/i.test(error.message)) throw friendlyError(error);
    const list: ReviewDraft[] =
      error || !Array.isArray(data)
        ? await this.batchDrafts(batchId)
        : (data as (ReviewDraft & { full_name?: string | null; email?: string | null; organisation_name?: string | null })[]).map((d) => ({
            ...d,
            person: { full_name: d.full_name ?? null, email: d.email ?? null },
            organisation: d.organisation_id ? { id: d.organisation_id, name: d.organisation_name ?? "" } : null,
          }));
    return this.withAssets(list);
  }

  /** 4.16 The asset each draft carries, if any. Until outreach_assets is deployed, the drafts as they are. */
  private async withAssets(list: ReviewDraft[]): Promise<ReviewDraft[]> {
    if (list.length === 0) return list;
    try {
      const { data, error } = await this.t("outreach_assets")
        .select("id, outbox_id, kind, slug, status, title, html")
        .eq("tenant_id", this.tenantId)
        .in("outbox_id", list.map((d) => d.id));
      if (error || !Array.isArray(data)) return list;
      const byDraft = new Map((data as OutreachAssetPreview[]).map((a) => [a.outbox_id, a]));
      return list.map((d) => (byDraft.has(d.id) ? { ...d, asset: byDraft.get(d.id) } : d));
    } catch {
      return list;
    }
  }

  /**
   * p_edits is keyed by outbox id: {"<id>": {"subject": "...", "body": "..."}}; only changed fields are sent. A rejection
   * may say why (wrong_person, too_pushy, not_a_fit, facts, tone, timing, legal, other; lib/review.ts): legal, tone
   * and too_pushy rejections hold back earned autonomy (E13).
   */
  /** 4.19 Bring a claimed preview website into this workspace's studio as a draft. */
  importClaimedSite(asset: string) {
    return this.act<{ imported: boolean; version?: number; again?: boolean }>("import_claimed_site", { asset });
  }

  decideDrafts(ids: string[], approve: boolean, note?: string, edits: Record<string, { subject?: string; body?: string }> = {}, rejectReason?: RejectReason) {
    if (ids.length === 0) throw new CrmError("Choose at least one message.");
    if (ids.length > 100) throw new CrmError("Decide at most 100 messages at a time.");
    return this.act<number>("decide_drafts", { ids, approve, ...(note ? { note } : {}), edits, ...(!approve && rejectReason ? { reject_reason: rejectReason } : {}) });
  }

  // ---------------------------------------------------------------- 4.22-4.23 send by hand
  /** Messages waiting for the owner to send by hand (crm_hand_sends). Until the migration is deployed, none. */
  async handSends(): Promise<HandSend[]> {
    const { data, error } = await this.sb.rpc("crm_hand_sends", { p_tenant: this.tenantId, p_status: "ready", p_limit: 100 });
    if (error && /Could not find|isn't available|does not exist|PGRST202/i.test(error.message)) return [];
    if (error) throw friendlyError(error);
    return Array.isArray(data) ? (data as HandSend[]) : [];
  }

  /** "I've sent it" (4.23): logs the touch. `channel` when it went another way; subject and body when changed. */
  markHandSent(id: string, sent: { channel?: HandSendChannel; subject?: string; body?: string } = {}) {
    return this.act<{ id: string; touch_id: string; channel: HandSendChannel; next_follow_up_at?: string }>("hand_send_sent", { id, ...sent });
  }

  skipHandSend(id: string, reason?: string) {
    return this.act<void>("hand_send_skip", { id, ...(reason ? { reason } : {}) });
  }

  editHandSend(id: string, body: string, subject?: string) {
    return this.act<void>("hand_send_edit", { id, body, ...(subject !== undefined ? { subject } : {}) });
  }

  outboxForOrganisation(org: string) {
    return this.rows<OutboxItem>(
      this.t("outbox")
        .select("id, channel, step_id, subject, body, status, batch_id, scheduled_for, sent_at, created_at, person:people(full_name, email)")
        .eq("tenant_id", this.tenantId)
        .eq("organisation_id", org)
        .order("created_at", { ascending: false })
        .limit(20),
    );
  }

  // ---------------------------------------------------------------- 6.11 replies
  message(id: string) {
    return this.one<Message>(
      this.t("messages")
        .select("id, channel, from_value, body, received_at, intent, intent_confidence, organisation_id, person_id, person:people(full_name), organisation:organisations(name)")
        .eq("tenant_id", this.tenantId)
        .eq("id", id)
        .maybeSingle(),
    );
  }

  decideReply(message: string, intent: ReplyIntent, followUpAt?: string, note?: string) {
    return this.act<string>("decide_reply", { message, intent, ...(followUpAt ? { follow_up_at: followUpAt } : {}), ...(note ? { note } : {}) });
  }

  /** 6.10, 6.11 Write the reply yourself: it is approved by writing it and replaces the agent's draft. */
  answerMessage(message: string, body: string, opts: { subject?: string; intent?: AnswerIntent; note?: string } = {}) {
    if (!body.trim()) throw new CrmError("Write the reply first.");
    return this.act<string>("answer_message", {
      message,
      body,
      ...(opts.subject?.trim() ? { subject: opts.subject.trim() } : {}),
      ...(opts.intent ? { intent: opts.intent } : {}),
      ...(opts.note?.trim() ? { note: opts.note.trim() } : {}),
    });
  }

  // ---------------------------------------------------------------- 9.6 documents
  dealDocument(id: string) {
    return this.one<DealDocument>(
      this.t("deal_documents").select("id, deal_id, kind, title, status, terms, review_note").eq("tenant_id", this.tenantId).eq("id", id).maybeSingle(),
    );
  }

  decideDocument(document: string, approve: boolean, note?: string) {
    return this.act<void>("decide_document", { document, approve, ...(note ? { note } : {}) });
  }

  /** A deal's documents and where each one is (draft, awaiting approval, sent, viewed, signed...). */
  dealDocuments(deal: string) {
    return this.rows<DealDocument>(
      this.t("deal_documents")
        .select("id, deal_id, kind, title, status, terms, review_note, provider, sent_at, signed_at, created_at")
        .eq("tenant_id", this.tenantId)
        .eq("deal_id", deal)
        .order("created_at", { ascending: false }),
    );
  }

  /** 9.2 Draft an NDA or proposal for the deal; the deal agent sends it through Documenso (after 9.6 if it is an approval kind). */
  requestDocument(deal: string, kind: "nda" | "proposal") {
    return this.act<{ document_id: string; status: string; existing: boolean; send_task: string | null }>("request_document", { deal, kind });
  }

  /** 9.W Open a deal for a firm. The server refuses a second open deal. */
  createDeal(organisation: string, opts: { stage?: string; value_estimate?: number | null; next_step?: string } = {}) {
    if (opts.value_estimate !== undefined && opts.value_estimate !== null && !(opts.value_estimate > 0)) {
      throw new CrmError("The value must be a positive amount, or left blank.");
    }
    return this.act<string>("create_deal", {
      organisation,
      ...(opts.stage ? { stage: opts.stage } : {}),
      ...(opts.value_estimate ? { value_estimate: opts.value_estimate } : {}),
      ...(opts.next_step?.trim() ? { next_step: opts.next_step.trim() } : {}),
    });
  }

  /** 9.D Proceed (next stage, or a later one), lost (a reason is needed) or hold until a date (YYYY-MM-DD). */
  decideDeal(deal: string, outcome: DealOutcome, opts: { reason?: string; stage?: string; holdUntil?: string } = {}) {
    const reason = opts.reason?.trim() ?? "";
    if (outcome === "lost" && !reason) throw new CrmError("Say why the deal was lost; it helps the next one.");
    return this.act<{ stage: string; task_id: string | null }>("decide_deal", {
      deal,
      outcome,
      ...(reason ? { reason } : {}),
      ...(outcome === "proceed" && opts.stage ? { stage: opts.stage } : {}),
      ...(outcome === "hold" && opts.holdUntil ? { hold_until: opts.holdUntil } : {}),
    });
  }

  // ---------------------------------------------------------------- Connect playbooks and ReConnect (migration 20261107090000)
  /** A firm's Connect playbook (crm_connect_playbook): its flavour, pace, first ask and whether an offer may be made yet.
   *  Pass the firm, or something of the firm's (a message, deal, person, meeting or task). */
  connectPlaybook(target: { organisation?: string | null; entityType?: string | null; entityId?: string | null }) {
    return this.rpc<Playbook | null>("crm_connect_playbook", {
      p_tenant: this.tenantId,
      p_organisation: target.organisation ?? null,
      p_entity_type: target.entityType ?? null,
      p_entity: target.entityId ?? null,
    });
  }

  /** 10.R Everyone in ReConnect and every lost deal's firm, with what happens next (crm_reconnect_board). */
  async reconnectBoard(): Promise<ReconnectStatus[]> {
    return (await this.rpc<ReconnectStatus[] | null>("crm_reconnect_board", { p_tenant: this.tenantId })) ?? [];
  }

  /** Save offers (9.11) and ReConnect offers (10.10), newest first, for a firm or a deal. */
  async connectOffers(opts: { organisation?: string; deal?: string } = {}): Promise<ConnectOffer[]> {
    return (await this.rpc<ConnectOffer[] | null>("crm_connect_offers", { p_tenant: this.tenantId, p_organisation: opts.organisation ?? null, p_deal: opts.deal ?? null })) ?? [];
  }

  /** 6.3 Replies due in Connect, by flavour, soonest first. */
  async repliesDue(): Promise<{ organisation_id: string; organisation: string; flavour: Flavour; message_id: string; received_at: string; due_by: string; overdue: boolean }[]> {
    return (await this.rpc("crm_connect_replies_due", { p_tenant: this.tenantId })) ?? [];
  }

  /** 9.12 Approve a save offer (optionally in your own words) or reject it with the reason, which closes the deal as lost. */
  decideSaveOffer(offer: string, approve: boolean, opts: { note?: string; body?: string } = {}) {
    const note = opts.note?.trim() ?? "";
    const body = opts.body?.trim() ?? "";
    if (!approve && !note) throw new CrmError("Say why you are rejecting the offer.");
    if (approve && body && body.length < 20) throw new CrmError("The email is too short to send.");
    return this.act<{ status: string }>("decide_save_offer", { offer, approve, ...(note ? { note } : {}), ...(approve && body ? { body } : {}) });
  }

  // ---------------------------------------------------------------- 9.13-9.15 Connect paths (migration 20261122090000)
  /** Conversations › Routes: each path with its blocks and campaigns, the firms at each block and waiting for a person,
   *  every campaign and its path, the templates for "Add a path", and the booking switch (crm_connect_paths_board). */
  connectPathsBoard(waitingLimit = 50) {
    return this.rpc<PathsBoard>("crm_connect_paths_board", { p_tenant: this.tenantId, p_waiting_limit: waitingLimit });
  }

  /** A firm's path run (its live one, else its latest), for the firm or a deal; null when it has none. */
  connectRun(target: { organisation?: string | null; deal?: string | null }) {
    return this.rpc<ConnectRun | null>("crm_connect_run", { p_tenant: this.tenantId, p_organisation: target.organisation ?? null, p_deal: target.deal ?? null });
  }

  /** 9.13 Start a firm on its path by the rules, or (a person) choose or change its path. */
  startPath(organisation: string, path?: string | null) {
    return this.act<StartPathResult>("start_path", { organisation, ...(path ? { path } : {}) });
  }

  /** 9.15 A person's step on the path is done (the path_step task's Done button): the firm's path moves on. At Qualify,
   * result says whether they are a fit, for the step's routes. */
  completePathBlock(run: string, block: BlockKey, note?: string, result?: PathOutcome) {
    const n = note?.trim() ?? "";
    if (n.length > 500) throw new CrmError("Keep the note under 500 characters.");
    return this.act<CompleteBlockResult>("complete_path_block", { run, block, ...(n ? { note: n } : {}), ...(result ? { result } : {}) });
  }

  /** 9.25 Where a firm goes next, when its path's route asks a person: a later step, yes, lost or ReConnect. */
  chooseRoute(run: string, to: string, reapproachDays?: number) {
    return this.act<CompleteBlockResult>("choose_route", { run, to, ...(reapproachDays ? { reapproach_days: reapproachDays } : {}) });
  }

  /** 9.14 "Firms on it now too": the firms already on these paths take the new steps once that version is approved. */
  /** 9.14 Move one firm onto the current steps of its path (its path changed after it started). */
  rerouteFirm(organisation: string) {
    return this.act<{ moved: boolean; status: string; block?: string }>("reroute_firm", { organisation });
  }

  requestReroute(version: number, paths: string[]) {
    return this.act<{ id: string; version: number; paths: string[] }>("request_reroute", { version, paths });
  }

  /** E13 "Send booking messages without waiting for me" (decision 7): meeting emails to auto, or back to approval. */
  setBookingAutonomy(on: boolean) {
    return this.act<{ kind: string; level: string; on: boolean; changed: boolean }>("set_booking_autonomy", { on });
  }

  // ---------------------------------------------------------------- 0.48 Campaigns as the spine (migration 20261229090000)
  /** Every campaign in Attract, Find, Network and ReConnect, with the path that lists it (crm_campaigns_all). */
  async campaignsAll(): Promise<CampaignSummary[]> {
    return (await this.rpc<CampaignSummary[] | null>("crm_campaigns_all", { p_tenant: this.tenantId })) ?? [];
  }

  /** Which CTAs, events or ReConnect offers are linked to which campaign: {id: campaign id} (crm_campaign_links). */
  async campaignLinks(kind: "cta" | "network_event" | "reconnect_offer"): Promise<Record<string, string>> {
    return (await this.rpc<Record<string, string> | null>("crm_campaign_links", { p_tenant: this.tenantId, p_kind: kind })) ?? {};
  }

  /** 0.48 Make a campaign for Attract, Network or ReConnect (Find's are made on Prospects › Campaigns). */
  addCampaign(module: "attract" | "network" | "reconnect", name: string) {
    const n = name.trim();
    if (!n) throw new CrmError("Give the campaign a name.");
    if (n.length > 120) throw new CrmError("Keep the name to 120 characters.");
    return this.act<{ id: string; module: string; name: string; status: string }>("add_campaign", { module, name: n });
  }

  /** 0.48 Rename an Attract, Network or ReConnect campaign. */
  renameCampaign(campaign: string, name: string) {
    const n = name.trim();
    if (!n) throw new CrmError("Give the campaign a name.");
    if (n.length > 120) throw new CrmError("Keep the name to 120 characters.");
    return this.act<{ id: string; name: string; changed: boolean }>("rename_campaign", { campaign, name: n });
  }

  /** 9.13 The Find campaigns whose Connect set-up nobody has looked at yet (Conversations › Routes). */
  connectSetupTodo() {
    return this.rpc<{ id: string; name: string; status: string }[]>("crm_connect_setup_todo", { p_tenant: this.tenantId });
  }

  /** 9.13 Keep a Find campaign's Connect set-up as it is: it comes off the list of campaigns to check. */
  checkCampaignConnect(campaign: string) {
    return this.act<{ campaign: string; checked: boolean }>("check_campaign_connect", { campaign });
  }

  /** 4.2 The sending style the drafting agent reads before each email: the business's, with the campaign's on top,
   * and the owner's latest edits it learns from (the campaign's first). */
  sendingStyle(campaign: string | null = null, examples = 5) {
    return this.rpc<SendingStyle>("crm_sending_style", { p_tenant: this.tenantId, p_campaign: campaign, p_examples: examples });
  }

  /** 4.2 Set a Find campaign's own tone, rules, greeting and sign-off ({} clears them). */
  setCampaignStyle(campaign: string, style: CampaignStyle) {
    return this.act<{ campaign: string; style: CampaignStyle }>("set_campaign_style", { campaign, style });
  }

  /** 0.48 Pause or resume an Attract, Network or ReConnect campaign. */
  pauseCampaign(campaign: string, paused: boolean) {
    return this.act<{ id: string; paused: boolean; changed: boolean }>("pause_campaign", { campaign, paused });
  }

  /** 0.48 Link a CTA (Attract), an event (Network) or a ReConnect offer to a campaign, or unlink it (campaign null). */
  linkCampaign(kind: "cta" | "network_event" | "reconnect_offer", id: string, campaign: string | null) {
    return this.act<{ linked: boolean; changed: boolean; leads_stamped?: number }>("link_campaign", { kind, id, campaign });
  }

  // ---------------------------------------------------------------- 9.16-9.19, 9.27 Ogma Quote (migrations 20261129100000, 20270220090000)
  /** Quotes › Price lists: every version of one book (the default when none is named), newest first, and whether an
   *  uploaded price list is being read. */
  async priceBook(book: string | null = null): Promise<PriceBook> {
    return (await this.rpc<PriceBook | null>("crm_price_versions", { p_tenant: this.tenantId, p_book: book })) ?? { check_first: 20, reading: false, versions: [] };
  }

  /** Quotes › Price lists: the business's books, each with its published version, drafts, quotes and flows. */
  async priceBooks(): Promise<PriceBookRow[]> {
    return (await this.rpc<PriceBookRow[] | null>("crm_price_books", { p_tenant: this.tenantId })) ?? [];
  }

  /** 9.16 A new price book, or a book's name, description and offer. */
  savePriceBook(fields: { name: string; description?: string | null; cta?: string | null }, book: string | null = null) {
    const description = fields.description?.trim() ?? "";
    return this.act<{ id: string; key: string; name: string; is_default: boolean }>("save_price_book", {
      ...(book ? { book } : {}),
      name: fields.name.trim(),
      ...(description ? { description } : {}),
      ...(fields.cta ? { cta: fields.cta } : {}),
    });
  }

  /** 9.16 The book a firm's quote uses unless another is given. */
  setDefaultPriceBook(book: string) {
    return this.act<{ id: string; changed: boolean }>("set_default_price_book", { book });
  }

  /** 9.16 Archive a book (the default can't be), or bring it back. */
  archivePriceBook(book: string, archived = true) {
    return this.act<{ id: string; changed: boolean }>("archive_price_book", { book, archived });
  }

  /** 9.16 Save a draft of a price book (a new version in a book, or changes to a draft). Answers with the checker's problems. */
  savePriceDraft(definition: PriceDefinition, opts: { version?: string | null; note?: string; book?: string | null } = {}) {
    const note = opts.note?.trim() ?? "";
    return this.act<{ id: string; version: number; status: string; problems: string[] }>("save_price_draft", {
      definition,
      ...(opts.version ? { version: opts.version } : {}),
      ...(note ? { note } : {}),
      ...(opts.book && !opts.version ? { book: opts.book } : {}),
    });
  }

  /** Quotes › Question flows: every flow with its versions (newest first). */
  async quoteFlows(): Promise<QuoteFlowRow[]> {
    return (await this.rpc<QuoteFlowRow[] | null>("crm_quote_flows", { p_tenant: this.tenantId })) ?? [];
  }

  /** 9.16 A new question flow (qualify, or quote for a book; it starts with an empty draft), or a flow's name. */
  saveQuoteFlow(fields: { name: string; purpose?: FlowPurpose; book?: string | null }, flow: string | null = null) {
    return this.act<{ id: string; key: string; name: string; purpose: FlowPurpose; draft_id?: string }>("save_quote_flow", {
      ...(flow ? { flow } : {}),
      name: fields.name.trim(),
      ...(fields.purpose && !flow ? { purpose: fields.purpose } : {}),
      ...(fields.book ? { book: fields.book } : {}),
    });
  }

  /** 9.16 Save a flow's draft (a new version, or changes to a draft). Answers with the checker's problems. */
  saveFlowDraft(flow: string, definition: FlowDefinition, version: string | null = null) {
    return this.act<{ id: string; version: number; status: string; problems: string[] }>("save_flow_draft", { flow, definition, ...(version ? { version } : {}) });
  }

  /** 9.16 Publish a flow's draft: refused in words until it checks out and supplies its price book. */
  publishFlowVersion(version: string) {
    return this.act<{ id: string; version: number; status: string; retired_version?: number }>("publish_flow_version", { version });
  }

  discardFlowDraft(version: string) {
    return this.act<{ status: string }>("discard_flow_draft", { version });
  }

  archiveQuoteFlow(flow: string, archived = true) {
    return this.act<{ id: string; changed: boolean }>("archive_quote_flow", { flow, archived });
  }

  /** The firm's question flows on the portal, newest first: each with its link, status and quote (crm_firm_quote_responses). */
  async firmQuoteResponses(organisation: string): Promise<FirmQuoteResponse[]> {
    return (await this.rpc<FirmQuoteResponse[] | null>("crm_firm_quote_responses", { p_tenant: this.tenantId, p_organisation: organisation })) ?? [];
  }

  /** One set of answers with its flow and where each answer came from (crm_quote_response). */
  quoteResponse(response: string) {
    return this.rpc<QuoteResponseRead | null>("crm_quote_response", { p_tenant: this.tenantId, p_response: response });
  }

  /** 9.26 A flow (a re-quote) for a firm on the portal: its private link, filled in from what the firm answered before. */
  startQuoteFlow(flow: string, organisation: string, person?: string | null) {
    return this.act<{ id: string; token: string; status: string }>("start_quote_flow", { flow, organisation, ...(person ? { person } : {}) });
  }

  /** 9.26 Email the customer their link (it waits at 4.10 like every Connect message). */
  sendQuoteFlow(response: string) {
    return this.act<{ outbox_id: string; already?: boolean }>("send_quote_flow", { response });
  }

  /** 1.49 The flow a quote call to action opens on the portal (null: its own address again). */
  setCtaFlow(cta: string, flow: string | null) {
    return this.act<{ id: string; flow_id: string | null }>("set_cta_flow", { cta, flow });
  }

  /** Quotes › Knowledge: the business's entries, newest first. */
  async quoteKnowledge(): Promise<KnowledgeEntry[]> {
    return (await this.rpc<KnowledgeEntry[] | null>("crm_quote_knowledge", { p_tenant: this.tenantId })) ?? [];
  }

  /** 9.16 A new knowledge entry, or changes to one (for the business, a book or a flow). */
  saveQuoteKnowledge(fields: { title: string; body: string; source?: string; book?: string | null; flow?: string | null; approved: boolean }, entry: string | null = null) {
    const source = fields.source?.trim() ?? "";
    return this.act<{ id: string; approved: boolean }>("save_quote_knowledge", {
      ...(entry ? { entry } : {}),
      title: fields.title.trim(),
      body: fields.body.trim(),
      ...(source ? { source } : {}),
      ...(fields.book ? { book: fields.book } : {}),
      ...(fields.flow ? { flow: fields.flow } : {}),
      approved: fields.approved,
    });
  }

  deleteQuoteKnowledge(entry: string) {
    return this.act<{ deleted: boolean }>("delete_quote_knowledge", { entry });
  }

  /** Quotes › Knowledge: the questions the assistant passed on, newest first (9.28). */
  async quoteQuestions(): Promise<QuoteQuestion[]> {
    return (await this.rpc<QuoteQuestion[] | null>("crm_quote_questions", { p_tenant: this.tenantId })) ?? [];
  }

  /** 9.16 Answer a question the assistant passed on: added as an approved entry for its flow, unless ``add`` is false. */
  answerQuoteQuestion(question: string, answer: string, title: string | null, add: boolean) {
    return this.act<{ id: string; knowledge_id?: string }>("answer_quote_question", { question, answer: answer.trim(), ...(title?.trim() ? { title: title.trim() } : {}), add });
  }

  /** 9.28 The owner's test box: what the assistant would answer for a flow (or the whole business), by the same code
   * customers get; counted against its own small limit and kept on no session. */
  tryAssistant(question: string, flow: string | null) {
    return this.act<AssistantTry>("try_assistant", { question: question.trim(), ...(flow ? { flow } : {}) });
  }

  /** 9.28 The weekly review: the questions customers asked in the last 7 days, with the answers and your verdicts. */
  async assistantReview(): Promise<AssistantReviewRow[]> {
    return (await this.rpc<AssistantReviewRow[] | null>("crm_assistant_review", { p_tenant: this.tenantId, p_days: 7 })) ?? [];
  }

  /** 9.28 Approve or flag one answer from the review; a flagged one can join the questions to answer. */
  reviewAssistantAnswer(response: string, at: string, verdict: "approved" | "flagged", toList = false) {
    return this.act<{ verdict: string; question_id?: string }>("review_assistant_answer", { response, at, verdict, to_list: toList });
  }

  /** 9.16 Publish a draft: refused with the problems in words until the checker finds none. */
  publishPriceVersion(version: string) {
    return this.act<{ id: string; version: number; status: string; retired_version?: number }>("publish_price_version", { version });
  }

  discardPriceDraft(version: string) {
    return this.act<{ status: string }>("discard_price_draft", { version });
  }

  /** 9.16 A price list (as text) for Ogma to draft a price book from, for you to check. */
  uploadPriceList(text: string, filename?: string) {
    const t = text.trim();
    if (t.length < 20) throw new CrmError("Paste or upload the price list first.");
    if (t.length > 60000) throw new CrmError("That price list is too long (60,000 characters at most). Split it.");
    return this.act<{ task_id: string; status: string }>("upload_price_list", { text: t, ...(filename ? { filename } : {}) });
  }

  /** A firm's (or a deal's) quotes, every revision, newest first. */
  async quotes(target: { organisation?: string | null; deal?: string | null } = {}): Promise<QuoteRow[]> {
    return (await this.rpc<QuoteRow[] | null>("crm_quotes", { p_tenant: this.tenantId, p_organisation: target.organisation ?? null, p_deal: target.deal ?? null, p_limit: 50 })) ?? [];
  }

  /** One quote in full: answers, the price, the questions and its timeline. */
  quote(id: string) {
    return this.rpc<QuoteRow | null>("crm_quote", { p_tenant: this.tenantId, p_quote: id });
  }

  /** 9.16 A quote for a firm with your answers, priced at once by the published version of the book (the default
   * book when none is given). */
  startQuote(organisation: string, answers: Answers, opts: { person?: string | null; book?: string | null } = {}) {
    return this.act<QuoteRow>("start_quote", { organisation, answers, ...(opts.person ? { person: opts.person } : {}), ...(opts.book ? { book: opts.book } : {}) });
  }

  /** 9.16 Confirm (or correct) the answers Ogma read from a conversation; the quote is priced at once. */
  confirmQuoteAnswers(quote: string, answers: Answers) {
    return this.act<QuoteRow>("confirm_quote_answers", { quote, answers });
  }

  /** 9.16 A new revision of a quote, with new answers, priced at once. */
  reviseQuote(quote: string, answers?: Answers) {
    return this.act<QuoteRow>("revise_quote", { quote, ...(answers ? { answers } : {}) });
  }

  /** 9.17 Approve a quote that waited for you, or withdraw it with the reason. */
  decideQuote(quote: string, approve: boolean, note?: string) {
    const n = note?.trim() ?? "";
    if (!approve && !n) throw new CrmError("Say why you are withdrawing the quote.");
    return this.act<QuoteRow>("decide_quote", { quote, approve, ...(n ? { note: n } : {}) });
  }

  // ---------------------------------------------------------------- 8.D, 8.4 meetings
  meeting(id: string) {
    return this.one<MeetingDetail>(
      this.t("meetings")
        .select("id, organisation_id, person_id, deal_id, starts_at, ends_at, status, location, next_steps, transcript_url, brief, organisation:organisations(name), person:people(full_name, email, role)")
        .eq("tenant_id", this.tenantId)
        .eq("id", id)
        .maybeSingle(),
    );
  }

  /** A firm's meetings, newest first (the CRM company card's Meetings, each opening the meeting card). */
  meetingsForOrganisation(organisation: string) {
    return this.rows<{ id: string; starts_at: string; status: string; next_steps: string | null; person: { full_name: string | null } | null }>(
      this.t("meetings")
        .select("id, starts_at, status, next_steps, person:people(full_name)")
        .eq("tenant_id", this.tenantId)
        .eq("organisation_id", organisation)
        .order("starts_at", { ascending: false })
        .limit(20),
    );
  }

  /** 8.D Did it happen? Notes (and a transcript link) given with "held" go to the deal agent for 8.4 and 8.5. */
  confirmAttendance(meeting: string, outcome: AttendanceOutcome, opts: { notes?: string; transcriptUrl?: string } = {}) {
    const url = opts.transcriptUrl?.trim() ?? "";
    if (url && !/^https:\/\/\S+$/.test(url)) throw new CrmError("The transcript link must start with https://.");
    return this.act<{ status: string; ignored?: boolean }>("confirm_attendance", {
      meeting,
      outcome,
      ...(opts.notes?.trim() ? { notes: opts.notes.trim() } : {}),
      ...(outcome === "held" && url ? { transcript_url: url } : {}),
    });
  }

  /** 8.4 Paste the notes or the transcript (or its link); the deal agent summarises them and extracts next steps. */
  submitTranscript(meeting: string, text: string, url?: string) {
    const t = text.trim();
    const u = url?.trim() ?? "";
    if (!t && !u) throw new CrmError("Paste the notes or the transcript, or give its link.");
    if (u && !/^https:\/\/\S+$/.test(u)) throw new CrmError("The transcript link must start with https://.");
    return this.act<string>("submit_transcript", { meeting, ...(t ? { text: t } : {}), ...(u ? { url: u } : {}) });
  }

  // ---------------------------------------------------------------- pipeline
  stages(pipeline: "lead" | "deal") {
    return this.rows<Stage>(
      this.t("pipeline_stages").select("pipeline, key, label, position, is_terminal").eq("tenant_id", this.tenantId).eq("pipeline", pipeline).order("position"),
    );
  }

  organisations(limit = 1000) {
    return this.rows<Organisation>(
      this.t("organisations").select("*").eq("tenant_id", this.tenantId).order("stage_changed_at", { ascending: false }).limit(limit),
    );
  }

  organisation(id: string) {
    return this.one<Organisation>(this.t("organisations").select("*").eq("tenant_id", this.tenantId).eq("id", id).maybeSingle());
  }

  setStage(org: string, stage: string, reason: string) {
    if (!reason.trim()) throw new CrmError("Add a short reason for the move.");
    return this.act<void>("set_stage", { organisation: org, stage, reason: reason.trim() });
  }

  deals() {
    return this.rows<Deal>(
      this.t("deals").select("id, organisation_id, stage, value_estimate, next_step, next_step_at, lost_reason, updated_at, organisation:organisations!deals_organisation_id_fkey(name)").eq("tenant_id", this.tenantId).order("updated_at", { ascending: false }),
    );
  }

  deal(id: string) {
    return this.one<Deal>(
      this.t("deals").select("id, organisation_id, stage, value_estimate, next_step, next_step_at, lost_reason, updated_at, organisation:organisations!deals_organisation_id_fkey(name)").eq("tenant_id", this.tenantId).eq("id", id).maybeSingle(),
    );
  }

  dealsForOrganisation(org: string) {
    return this.rows<Deal>(
      this.t("deals").select("id, organisation_id, stage, value_estimate, next_step, next_step_at, lost_reason, updated_at").eq("tenant_id", this.tenantId).eq("organisation_id", org).order("updated_at", { ascending: false }),
    );
  }

  /**
   * Moves a deal. A lost deal needs a reason (crm_upsert_deal refuses otherwise); the reason is logged in the event either way.
   * A won or lost deal only moves with `reopen` (to an open stage); the database refuses otherwise, and refuses a reopen
   * while the firm has another open deal.
   */
  moveDeal(deal: string, stage: string, reason: string, reopen = false) {
    if (!reason.trim()) throw new CrmError("Add a short reason for the move.");
    return this.act<string>("move_deal", {
      deal, stage, reason: reason.trim(), ...(stage === "lost" ? { lost_reason: reason.trim() } : {}), ...(reopen ? { reopen: true } : {}),
    });
  }

  // ---------------------------------------------------------------- firm page
  people(org?: string) {
    let q = this.t("people").select("id, organisation_id, full_name, role, is_primary, email, email_status, phone, linkedin_url, tags, created_at").eq("tenant_id", this.tenantId);
    if (org) q = q.eq("organisation_id", org);
    return this.rows<Person>(q.order("is_primary", { ascending: false }).limit(1000));
  }

  signals(org: string) {
    return this.rows<Signal>(
      this.t("signals").select("id, kind, source, title, url, weight, observed_at, detail").eq("tenant_id", this.tenantId).eq("organisation_id", org).order("observed_at", { ascending: false }).limit(50),
    );
  }

  enrolments(org: string) {
    return this.rows<Enrolment>(
      this.t("enrolments").select("id, status, current_step, next_touch_at, stopped_reason, person:people(full_name), sequence:sequences(name)").eq("tenant_id", this.tenantId).eq("organisation_id", org).order("started_at", { ascending: false }),
    );
  }

  timeline(org: string, limit = 100) {
    return this.rpc<TimelineEntry[]>("crm_timeline", { p_tenant: this.tenantId, p_organisation: org, p_person: null, p_limit: limit });
  }

  addNote(org: string, body: string) {
    if (!body.trim()) throw new CrmError("Write the note first.");
    return this.act<string>("add_note", { entity_type: "organisation", entity: org, body: body.trim() });
  }

  // ---------------------------------------------------------------- 3.10 people and tags
  /** The business's tags, in order, with how many people carry each. */
  tagDefinitions() {
    return this.rpc<ContactTag[]>("crm_tag_definitions", { p_tenant: this.tenantId });
  }

  /** People across every firm: each word must match the name, email, role, phone or firm; tags means all of them. */
  async searchPeople(q: PeopleQuery = {}): Promise<PersonSearch> {
    const r = await this.rpc<PersonSearch | null>("crm_search_people", {
      p_tenant: this.tenantId,
      p_query: q.query?.trim() || null,
      p_tags: q.tags && q.tags.length ? q.tags : null,
      p_organisation: q.organisation || null,
      p_limit: q.limit ?? 100,
      p_offset: q.offset ?? 0,
      p_source: q.source || null,
      p_introduced_by: q.introducedBy || null,
      ...(q.channel ? { p_inbound_channel: q.channel } : {}),
      ...(q.module ? { p_module: q.module } : {}),
    });
    return { total: Number(r?.total ?? 0), rows: r?.rows ?? [] };
  }

  /** Every matching person (for a CSV), fetched 500 at a time, up to `max`. */
  async allPeople(q: Omit<PeopleQuery, "limit" | "offset"> = {}, max = 10_000): Promise<PersonSearch> {
    const first = await this.searchPeople({ ...q, limit: 500, offset: 0 });
    const rows = [...first.rows];
    while (rows.length < Math.min(first.total, max)) {
      const next = await this.searchPeople({ ...q, limit: 500, offset: rows.length });
      if (!next.rows.length) break;
      rows.push(...next.rows);
    }
    return { total: first.total, rows: rows.slice(0, max) };
  }

  person(id: string) {
    return this.rpc<PersonContext | null>("crm_person_context", { p_tenant: this.tenantId, p_person: id });
  }

  personTimeline(person: string, limit = 100) {
    return this.rpc<TimelineEntry[]>("crm_timeline", { p_tenant: this.tenantId, p_organisation: null, p_person: person, p_limit: limit });
  }

  /** Adds and removes tags on one person; returns their tags. Unknown tags are refused by the database. */
  async setPersonTags(person: string, add: string[], remove: string[] = []): Promise<string[]> {
    const a = cleanTags(add);
    const r = cleanTags(remove);
    if (!a.length && !r.length) throw new CrmError("Choose a tag first.");
    const out = await this.act<string[] | null>("set_person_tags", { person, add: a, remove: r });
    return out ?? [];
  }

  /** The same change on many people (at most 1,000). Returns how many changed. */
  async tagPeople(people: string[], add: string[], remove: string[] = []): Promise<number> {
    const ids = [...new Set(people)];
    if (!ids.length) throw new CrmError("Choose at least one person.");
    if (ids.length > MAX_PEOPLE_PER_TAGGING) throw new CrmError("Tag at most 1,000 people at a time.");
    const a = cleanTags(add);
    const r = cleanTags(remove);
    if (!a.length && !r.length) throw new CrmError("Choose a tag first.");
    return Number((await this.act<number>("tag_people", { people: ids, add: a, remove: r })) ?? 0);
  }

  /** Adds or edits a tag. Fields left undefined are kept; "" clears the colour or description. */
  upsertTag(def: { key: string; label?: string; colour?: string | null; description?: string | null; sort_order?: number }) {
    const key = def.key.trim().toLowerCase();
    if (!TAG_KEY_RE.test(key)) throw new CrmError("A tag key uses lower case letters, numbers, - or _ (up to 40), starting with a letter or number.");
    if (def.label !== undefined && !def.label.trim()) throw new CrmError("Give the tag a name.");
    if (def.colour && !/^#[0-9a-f]{6}$/i.test(def.colour)) throw new CrmError("Pick a colour like #4e565e.");
    const params: Record<string, unknown> = { key };
    if (def.label !== undefined) params.label = def.label.trim();
    if (def.colour !== undefined) params.colour = def.colour ?? "";
    if (def.description !== undefined) params.description = def.description ?? "";
    if (def.sort_order !== undefined) params.sort_order = def.sort_order;
    return this.act<string>("upsert_tag_definition", params);
  }

  /** Deletes a tag. While people carry it the server refuses unless force, which removes it from them first. */
  async deleteTag(key: string, force = false): Promise<number> {
    return Number((await this.act<number>("delete_tag_definition", { key, ...(force ? { force: true } : {}) })) ?? 0);
  }

  // ---------------------------------------------------------------- 3.11 contact sources and connections
  async contactSources(): Promise<ContactSource[]> {
    return (await this.rpc<ContactSource[] | null>("crm_contact_sources", {})) ?? [];
  }

  async linkKinds(): Promise<LinkKind[]> {
    return (await this.rpc<LinkKind[] | null>("crm_link_kinds", {})) ?? [];
  }

  async personNetwork(person: string, depth = 2, limit = 60): Promise<PersonNetwork> {
    const r = await this.rpc<PersonNetwork | null>("crm_person_network", { p_tenant: this.tenantId, p_person: person, p_depth: depth, p_limit: limit });
    return { centre: r?.centre ?? person, nodes: r?.nodes ?? [], edges: r?.edges ?? [] };
  }

  /** A firm's people with how we got them, and the outside people connected to the firm. */
  async firmContext(org: string): Promise<FirmContext> {
    const r = await this.rpc<Partial<FirmContext> | null>("crm_organisation_context", { p_tenant: this.tenantId, p_organisation: org });
    return { people: r?.people ?? [], connections: r?.connections ?? [] };
  }

  /** Firms by part of the name, for pickers. */
  firms(q: string, limit = 20) {
    const term = q.trim().replace(/[%_\\,()]/g, " ").trim();
    let query = this.t("organisations").select("id, name, city, kind, stage").eq("tenant_id", this.tenantId);
    if (term) query = query.ilike("name", `%${term}%`);
    return this.rows<{ id: string; name: string; city: string | null; kind: string; stage: string }>(query.order("name").limit(limit));
  }

  /** How we got a contact. Fields left undefined are kept; source_detail "" clears it. */
  setPersonSource(c: SourceChange) {
    const params: Record<string, unknown> = { person: c.person };
    if (c.source !== undefined && c.source.trim()) params.source = c.source.trim();
    if (c.source_detail !== undefined) params.source_detail = c.source_detail.trim();
    if (c.connected_on) {
      if (!DATE_RE.test(c.connected_on)) throw new CrmError("Give the date as a day, month and year.");
      params.connected_on = c.connected_on;
    }
    if (c.introduced_by) {
      if (c.introduced_by === c.person) throw new CrmError("Someone can't introduce themselves.");
      params.introduced_by = c.introduced_by;
    } else if (c.clear_introducer) params.clear_introducer = true;
    return this.act<void>("set_person_source", params);
  }

  /** Links a person to another person or a firm. Returns the link's id. */
  linkPeople(c: LinkChange) {
    if (!(LINK_KINDS as readonly string[]).includes(c.kind)) throw new CrmError("Choose how they are connected.");
    if (Boolean(c.to_person) === Boolean(c.to_organisation)) throw new CrmError("Choose one person or one firm to connect to.");
    if (c.to_person && c.to_person === c.from) throw new CrmError("Someone can't be connected to themselves.");
    if (c.since && !DATE_RE.test(c.since)) throw new CrmError("Give the date as a day, month and year.");
    return this.act<string>("link_people", {
      from: c.from,
      kind: c.kind,
      ...(c.to_person ? { to_person: c.to_person } : { to_organisation: c.to_organisation }),
      ...(c.note?.trim() ? { note: c.note.trim() } : {}),
      ...(c.since ? { since: c.since } : {}),
    });
  }

  unlinkPeople(link: string) {
    return this.act<void>("unlink_people", { link });
  }

  /** E9 Erase a person (GDPR). Irreversible: the screen asks first. A hashed suppression stays so they are never contacted. */
  erasePerson(person: string, reason?: string) {
    const r = reason?.trim();
    return this.act<{ status: string } | null>("erase_person", { person, confirm: true, ...(r ? { reason: r.slice(0, 200) } : {}) });
  }

  // ---------------------------------------------------------------- 0.45, 0.46 contacts by hand
  /** 0.45 Add a person by hand. A match already in the CRM comes back as {status: "duplicate"} and nothing is saved. */
  addContact(data: ContactInput & ContactExtras) {
    if (!data.full_name?.trim()) throw new CrmError("Give their name.");
    return this.act<AddResult>("add_contact", compact(data) as unknown as Record<string, unknown>);
  }

  /** 0.45 Edit a person: only the fields given change; null or "" clears one. */
  updateContact(person: string, data: ContactEdit & ContactExtras) {
    return this.act<{ status: string; changed: string[] }>("update_contact", { person, ...data });
  }

  /** 0.45 Add a firm by hand. A match comes back as {status: "duplicate"}. */
  addFirm(data: FirmInput & FirmExtras) {
    if (!data.name?.trim()) throw new CrmError("Give the firm a name.");
    return this.act<AddResult>("add_firm", compact(data) as unknown as Record<string, unknown>);
  }

  /** 0.45 Edit a firm: only the fields given change; null or "" clears one. */
  updateFirm(org: string, data: FirmEdit & FirmExtras) {
    return this.act<{ status: string; changed: string[] }>("update_firm", { organisation: org, ...data });
  }

  /** 0.46 Where things stand with a person: a note, their firm's stage, last contacted, the next step and its date. */
  noteLatest(person: string, data: LatestInput) {
    return this.act<{ status: string; changed: string[] }>("note_latest", { person, ...data });
  }

  /** 0.45 Add the people on a contact file to the CRM (at most 1,000 rows a call), as known contacts unless known is false. */
  addContacts(rows: FileContactRow[], known = true) {
    return this.act<AddContactsResult>("add_contacts", { rows, known });
  }

  /** E12 Claude spend per model since a date (default the last 30 days), with an estimated cost. */
  modelSpend(since?: string) {
    return this.rpc<ModelSpend[]>("crm_model_spend", { p_tenant: this.tenantId, ...(since ? { p_since: since } : {}) });
  }

  // ---------------------------------------------------------------- E13 earned autonomy, E14 spending caps
  /** Every kind of message with its level, what people decided since it was set, and whether it may move up. */
  autonomyStats() {
    return this.rpc<AutonomyKind[]>("crm_autonomy_stats", { p_tenant: this.tenantId });
  }

  /** What the system approved by itself, newest first (default the last 14 days). */
  autoApproved(since?: string, limit = 100) {
    return this.rpc<AutoApproved[]>("crm_auto_approved", { p_tenant: this.tenantId, ...(since ? { p_since: since } : {}), p_limit: limit });
  }

  /** Accept or decline a proposal to move one kind up one level. The database checks it still qualifies. */
  decideAutonomy(task: string, accept: boolean, note?: string) {
    const n = note?.trim();
    return this.act<{ status: string; kind: string; level: string; blockers?: string[] }>("decide_autonomy", { task, accept, ...(n ? { note: n.slice(0, 500) } : {}) });
  }

  /** Back to approval for every message of one kind, at once. */
  demoteAutonomy(kind: string, reason?: string) {
    const r = reason?.trim();
    return this.act<number>("demote_autonomy", { kind, ...(r ? { reason: r.slice(0, 500) } : {}) });
  }

  /** The kill switch: on sends every kind back to approval and holds everything until it is off. */
  setAutonomyKill(on: boolean, reason?: string) {
    const r = reason?.trim();
    return this.act<{ kill: boolean; demoted: number }>("set_autonomy_kill", { on, ...(r ? { reason: r.slice(0, 500) } : {}) });
  }

  // ---------------------------------------------------------------- modules (docs/SYSTEM_MAP.md section 5)
  /** The module of the given firms and people, worked out from their stage (null: all of them; []: none). */
  async modules(organisations: string[] | null = null, people: string[] | null = []): Promise<ModulesRead> {
    const r = await this.rpc<Partial<ModulesRead> | null>("crm_modules", { p_tenant: this.tenantId, p_organisations: organisations, p_people: people });
    return {
      modules: r?.modules ?? [],
      organisations: r?.organisations ?? {},
      people: r?.people ?? {},
      counts: { organisations: r?.counts?.organisations ?? {}, people: r?.counts?.people ?? {} },
    };
  }

  /** One owner at a time (migration 20261109090000): who owns a firm now, and the last thing the rule stopped or held
   * for it (crm_owner_status). Null for a firm this business does not have. */
  async ownerStatus(organisation: string): Promise<OwnerStatus | null> {
    const r = await this.rpc<Partial<OwnerStatus> | null>("crm_owner_status", { p_tenant: this.tenantId, p_organisation: organisation, p_person: null });
    if (!r) return null;
    return { ...r, module: r.module ?? null, one_owner: r.one_owner !== false, always_allowed: r.always_allowed ?? [], handoffs: r.handoffs ?? [] };
  }

  // ---------------------------------------------------------------- 10.8 sales performance
  /** The Sales performance page in one read: headline numbers against target, the funnel by pool, days per step,
   * deals gone quiet, what the agents did, the notes (written from the numbers) and the pinned answers. */
  performance(days = 30) {
    return this.rpc<Performance>("crm_performance", { p_tenant: this.tenantId, p_days: days });
  }

  /** E56 speed to lead (migration 20261125090000): seconds to the instant reply, minutes to the personal reply against
   * the target, and the slowest enquiries still waiting, for the period. */
  async speedToLead(days = 30): Promise<SpeedToLead> {
    return readSpeedToLead(await this.rpc<unknown>("crm_speed_to_lead", { p_tenant: this.tenantId, p_days: days }), days);
  }

  /** E62 owner minutes (migration 20261202090000): the week's minutes in Ogma against last week, by module and task
   * kind, things decided and minutes per decision. `weekStart` is any day of the week wanted (default this week). */
  async ownerMinutes(weekStart?: string): Promise<OwnerMinutes> {
    return readOwnerMinutes(await this.rpc<unknown>("crm_owner_minutes", { p_tenant: this.tenantId, ...(weekStart ? { p_week_start: weekStart } : {}) }));
  }

  /** E62 A batch of measured time (lib/ownerTimeTracker.tsx): durations, task ids, pages and days only. */
  async recordOwnerTime(entries: TimeEntry[]): Promise<void> {
    if (entries.length === 0) return;
    await this.act<unknown>("record_owner_time", { entries });
  }

  /** One metric from the catalogue (crm_metric_catalogue), calculated by the database. */
  // ---------------------------------------------------------------- Find: this week and results (migration 20261112110000)
  /** 2.21 This week's batch: firms and people added, drafts made, and who was left out and why. */
  findWeekSummary(since?: string) {
    return this.rpc<WeekSummary>("crm_find_week_summary", { p_tenant: this.tenantId, p_since: since ?? null });
  }

  /** The rows behind one number on the week card: firms, people, drafts, or an exclusion reason. */
  async findWeekList(since: string, what: string, limit = 50) {
    return (await this.rpc<WeekRow[] | null>("crm_find_week_list", { p_tenant: this.tenantId, p_since: since, p_what: what, p_limit: limit })) ?? [];
  }

  /** 10.8 Find results for a period (YYYY-MM-DD; default the last 30 days). */
  findResults(from?: string, to?: string) {
    return this.rpc<FindResults>("crm_find_results", { p_tenant: this.tenantId, p_from: from ?? null, p_to: to ?? null });
  }

  /** 4.21 Reply and positive-reply rates per send-time slot for a period (crm_send_time_results, migration 20261126090000). */
  sendTimeResults(from?: string, to?: string) {
    return this.rpc<SendTimeResults>("crm_send_time_results", { p_tenant: this.tenantId, p_from: from ?? null, p_to: to ?? null });
  }

  // ---------------------------------------------------------------- Mailbox health (E57, E58; migration 20261126100000)
  /** Each mailbox's sends against its cap and ramp, bounce, unsubscribe, negative-reply and complaint rates, blocklists and pauses. */
  mailboxHealth() {
    return this.rpc<MailboxHealth>("crm_mailbox_health", { p_tenant: this.tenantId });
  }

  /** Resume a mailbox paused for complaint signals or a blocklist listing (a person only). */
  resumeMailbox(mailbox: string, reason?: "complaints" | "blocklist" | null, note?: string | null) {
    return this.act<{ resumed: number; still_held: number }>("resume_mailbox", {
      mailbox,
      ...(reason ? { reason } : {}),
      ...(note?.trim() ? { note: note.trim().slice(0, 500) } : {}),
    });
  }

  metricQuery(spec: MetricSpec) {
    return this.rpc<MetricResult>("crm_metric_query", { p_tenant: this.tenantId, p_spec: spec });
  }

  /** Pin an ask-bar answer to the page (twelve at most). */
  pinPerformance(title: string, spec: MetricSpec, question?: string | null) {
    const q = question?.trim();
    return this.act<{ id: string; title: string; spec: MetricSpec }>("pin_performance", {
      title: title.trim().slice(0, 200),
      spec,
      ...(q ? { question: q.slice(0, 500) } : {}),
    });
  }

  unpinPerformance(pinId: string) {
    return this.act<void>("unpin_performance", { pin: pinId });
  }

  // ---------------------------------------------------------------- section pages (0.R, E15)
  /** Content of some kinds (social posts, newsletters), newest first: for the Attract channel pages. */
  contentOfKinds(kinds: string[], limit = 20) {
    return this.rows<ContentItem>(
      this.t("content_items").select(`${CONTENT_COLUMNS}, created_at`).eq("tenant_id", this.tenantId).in("kind", kinds).order("created_at", { ascending: false }).limit(limit),
    );
  }

  /** 2.4 News and signals across every firm, newest first (listening, migration 10). */
  recentSignals(limit = 50) {
    return this.rows<{ id: string; organisation_id: string | null; kind: string; source: string | null; title: string | null; url: string | null; weight: number | null; observed_at: string }>(
      this.t("signals").select("id, organisation_id, kind, source, title, url, weight, observed_at").eq("tenant_id", this.tenantId).order("observed_at", { ascending: false }).limit(limit),
    );
  }

  /** 2.7 How each introducer has done: people and firms introduced, meetings held, deals and wins (migration 16). */
  introducerPerformance() {
    return this.rows<{ person_id: string; introducer: string | null; firm: string | null; tags: string | null; people_introduced: number; firms_introduced: number; meetings_held: number; deals: number; won: number; last_introduction_on: string | null }>(
      this.t("v_introducer_performance").select("*").eq("tenant_id", this.tenantId).order("people_introduced", { ascending: false }),
    );
  }

  /** 5.W Messages received (replies and enquiries), newest first. */
  inboundMessages(limit = 50) {
    return this.rows<{ id: string; channel: string; from_value: string | null; body: string | null; received_at: string; intent: string | null; handled_at: string | null; organisation_id: string | null; person_id: string | null }>(
      this.t("messages").select("id, channel, from_value, body, received_at, intent, handled_at, organisation_id, person_id").eq("tenant_id", this.tenantId).order("received_at", { ascending: false }).limit(limit),
    );
  }

  // ---------------------------------------------------------------- 2.16-2.21, E18, E19 the prospect database
  /** The niches the prospect database holds, with labels and live counts, then Ogma's sectors and sub-sectors with
   *  their profiled firms (N-01: what Find prospects can search; crm_ref_taxonomy_counts, best effort). */
  async refNiches() {
    const [niches, sectors] = await Promise.all([
      this.rpc<RefNiche[]>("crm_ref_niches", {}),
      this.rpc<RefSectorCount[] | null>("crm_ref_taxonomy_counts", {}).catch(() => null),
    ]);
    return withSectors(niches ?? [], sectors ?? []);
  }

  /** 2.17 Search the prospect database for this business (never returns contact points). */
  /** The firm facts (#549) let an "any trade" search (facts, no niches) load its firms; without them nothing extra is sent. */
  async refSearch(q: { niches?: string[] | null; countries?: string[] | null; regions?: string[] | null; query?: string | null; facts?: FirmFacts; limit?: number; offset?: number }) {
    const r = await this.rpc<{ total: number; rows: RefFirm[] } | null>("crm_ref_search", {
      p_tenant: this.tenantId, p_niches: q.niches?.length ? q.niches : null, p_countries: q.countries?.length ? q.countries : null,
      p_regions: q.regions?.length ? q.regions : null, p_query: q.query?.trim() || null, p_limit: q.limit ?? 200, p_offset: q.offset ?? 0,
      ...factArgs(q.facts),
    });
    return { total: Number(r?.total ?? 0), rows: r?.rows ?? [] };
  }

  /** A plain search's recorded firms in the search's order (crm_prospect_firms), with any already in the CRM marked. */
  async prospectFirms(search: string, limit = 200) {
    const r = await this.rpc<{ total: number; rows: RefFirm[] } | null>("crm_prospect_firms", {
      p_tenant: this.tenantId, p_search: search, p_limit: limit, p_offset: 0,
    });
    return { total: Number(r?.total ?? 0), rows: r?.rows ?? [] };
  }

  /** 2.38 How far a search has got: the sample ranked first, then the rest (crm_prospect_progress). */
  prospectProgress(search: string) {
    return this.rpc<SearchProgress>("crm_prospect_progress", { p_tenant: this.tenantId, p_search: search });
  }

  prospectSearches(limit = 10) {
    return this.rows<ProspectSearch>(
      this.t("prospect_searches").select("id, query, plan, estimate, status, budget_usd, created_by, decided_by, created_at")
        .eq("tenant_id", this.tenantId).order("created_at", { ascending: false }).limit(limit),
    );
  }

  prospectResults(search: string) {
    return this.rows<ProspectResult>(
      this.t("prospect_results").select("ref_firm_id, origin, decision, reason, organisation_id, excluded_by, ranked_at").eq("tenant_id", this.tenantId).eq("search_id", search),
    );
  }

  /** 2.38 The ranked list for a search with criteria: best first, with reasons and the contact to try. */
  prospectRanked(search: string, limit = 200) {
    return this.rpc<RankedList>("crm_prospect_ranked", { p_tenant: this.tenantId, p_search: search, p_limit: limit, p_offset: 0 });
  }

  /** 2.38 One page of one of the review list's three groups, best first, with the counts of all three (group null: the counts only). */
  prospectRankedGroup(search: string, group: RankedGroup | null, limit = 50, offset = 0) {
    return this.rpc<GroupedList>("crm_prospect_ranked_groups", { p_tenant: this.tenantId, p_search: search, p_group: group, p_limit: limit, p_offset: offset });
  }

  /** 2.38 Every firm of one group, best first, not yet in the CRM or crossed out ("Tick all"). */
  async prospectGroupIds(search: string, group: RankedGroup) {
    return (await this.rpc<string[] | null>("crm_prospect_group_ids", { p_tenant: this.tenantId, p_search: search, p_group: group, p_limit: 2000 })) ?? [];
  }

  /** 2.19 Change a waiting search's must-haves and nice-to-haves (Ogma's niche check is kept). Before approval only. */
  setProspectCriteria(search: string, criteria: Partial<RankCriterion>[]) {
    return this.act<ProspectSearch["plan"]>("set_prospect_criteria", { search, criteria });
  }

  /** 2.19 "Copy and change": a new search like this one, waiting for approval, whose criteria can be changed. */
  copyProspectSearch(search: string) {
    return this.act<{ search_id: string; title: string }>("copy_prospect_search", { search });
  }

  /** 2.19 Approve the search and its spend, or cancel it (a human gate). */
  decideProspectSearch(search: string, approve: boolean, budgetUsd?: number) {
    return this.act<string>("decide_prospect_search", { search, approve, ...(budgetUsd !== undefined ? { budget_usd: budgetUsd } : {}) });
  }

  /** 2.39 / 10.20 Each approved search's pool (strong matches left, the next batch, runway) and reply batches. */
  async searchHealth(search?: string) {
    return (await this.rpc<SearchHealth | null>("crm_search_health", { p_tenant: this.tenantId, p_search: search ?? null }))
      ?? { settings: { pool: {}, replies: {} }, time_zone: "Europe/London", searches: [] };
  }

  /** 2.39 A suggestion tapped: a new search with one change, waiting for approval (2.19). Nothing runs before that. */
  widenProspectSearch(search: string, change: { kind: WidenKind; value?: string }) {
    return this.act<{ search_id: string; firms: number; title: string }>("widen_prospect_search", { search, change });
  }

  /** 2.21 Copy picked firms into this CRM. Person-finding then runs here (3.x). Firms the business already has a
   *  relationship with, or the owner already knows, are left out (by_reason); near matches wait for "Same firm?" (review). */
  pickProspects(search: string, firms: string[]) {
    return this.act<{ copied: number; linked: number; skipped: number; by_reason?: Record<string, number>; review?: number }>("pick_prospects", { search, firms });
  }

  /** 2.32 Answer "Same firm?" for a firm found that looks like one already held. */
  answerSameFirm(task: string, same: boolean) {
    return this.act<{ same: boolean; organisation_id: string }>("answer_same_firm", { task, same });
  }

  /** 0.28 Import a batch of the owner's own contacts (at most 1,000 rows a call). */
  importContacts(rows: ImportRow[]) {
    return this.act<{ rows: number; customers: number; organisations: number; people: number; skipped: number; known_added: number; known_updated: number }>(
      "import_contacts",
      { rows },
    );
  }

  /** 0.27-0.29 Who the business already knows: counts by kind and source. */
  knownContactsSummary() {
    return this.rpc<KnownContactsSummary>("crm_known_contacts_summary", { p_tenant: this.tenantId });
  }

  // ---------------------------------------------------------------- relationship history (0.40-0.43, 10.14, 10.15)
  /** 10.15 Spoken to before: people with past conversations, newest first (crm_spoken_before). */
  async spokenBefore(filters: SpokenFilters = {}, limit = 200): Promise<SpokenRow[]> {
    return (await this.rpc<SpokenRow[] | null>("crm_spoken_before", { p_tenant: this.tenantId, p_filters: filterParams(filters), p_limit: limit })) ?? [];
  }

  /** One person's past conversations and their summary (crm_relationship_history). */
  relationshipHistory(person: string, limit = 50) {
    return this.rpc<RelationshipHistory | null>("crm_relationship_history", { p_tenant: this.tenantId, p_person: person, p_organisation: null, p_limit: limit });
  }

  /** "Bring in your history": the options, counts by source, uploads and chats waiting for the owner. */
  async historyStatus(): Promise<HistoryStatus> {
    return readHistoryStatus(await this.rpc<unknown>("crm_history_status", { p_tenant: this.tenantId }));
  }

  /** 0.40-0.42 What the owner allows (people only). */
  setHistoryOptions(options: Partial<Pick<HistoryOptions, "crm" | "read_mailbox" | "create_people" | "keep_full_text" | "months">>) {
    return this.act<HistoryOptions>("set_history_options", { options });
  }

  /** 0.42 Uploads a WhatsApp export and asks Ogma to read it: registered first (act register_history_upload gives the one
   *  path the private history-uploads bucket takes it at, for an hour), then uploaded there, then confirmed. A file
   *  whose upload never finishes is deleted by the hourly sweep; the file is deleted once read. */
  async uploadWhatsapp(file: File, ownerName?: string): Promise<string> {
    const problem = uploadProblem(file);
    if (problem) throw new CrmError(problem);
    const fileName = file.name.slice(0, 200);
    const owner = ownerName?.trim() ? { owner_name: ownerName.trim().slice(0, 120) } : {};
    const reg = await this.act<{ upload_id: string; path: string }>("register_history_upload", { file_name: fileName, ...owner });
    const { error } = await this.sb.storage.from(HISTORY_BUCKET).upload(reg.path, file, { upsert: false, contentType: file.type || "application/octet-stream" });
    if (error) throw friendlyError(error);
    return this.act<string>("add_history_upload", { path: reg.path, file_name: fileName, ...owner });
  }

  /** 0.42 Who a waiting WhatsApp chat is with: a person, a new person, or nobody. */
  confirmHistoryChat(chat: string, answer: { person: string } | { create: true } | { skip: true }) {
    return this.act<{ status: string; person_id?: string; imported?: number }>("confirm_history_chat", { chat, ...answer });
  }

  // ---------------------------------------------------------------- signals (2.35, 2.36, 10.16, 10.17; docs/specs/SIGNALS.md)
  /** 2.35 The roles a play could replace; the owner's list is final (the engine never replaces it). */
  setPlaySignals(play: string, signals: { roles: PlayRole[]; exclude: string[] }) {
    return this.act<unknown>("set_play_signals", { play, signals: { roles: signals.roles, exclude: signals.exclude } });
  }

  /** 2.35 Firms hiring for a role a play could replace, newest first (crm_hiring_signals). */
  async hiringSignals(since?: string, limit = 50): Promise<HiringRow[]> {
    return (await this.rpc<HiringRow[] | null>("crm_hiring_signals", { p_tenant: this.tenantId, p_since: since ?? null, p_limit: limit })) ?? [];
  }

  /** 10.17 Contacts who may have moved job: suggested (default), confirmed, dismissed or all (crm_person_moves). */
  async personMoves(status: "suggested" | "confirmed" | "dismissed" | "all" = "suggested", limit = 100): Promise<MoveRow[]> {
    return (await this.rpc<MoveRow[] | null>("crm_person_moves", { p_tenant: this.tenantId, p_status: status, p_limit: limit })) ?? [];
  }

  /** 10.17 The owner's decision on a move: confirm, dismiss, or plan a follow-up (always approved before sending). */
  decideMove(move: string, decision: MoveDecision, note?: string, acceptEmail = false) {
    return this.act<MoveResult>("decide_move", {
      move, decision, ...(note?.trim() ? { note: note.trim() } : {}), ...(acceptEmail ? { accept_email: true } : {}),
    });
  }

  /** 10.15 Plan follow-ups for the people ticked (at most 50). */
  planFollowUps(people: string[], note?: string) {
    return this.act<PlanResult>("plan_follow_ups", { people, ...(note?.trim() ? { note: note.trim() } : {}) });
  }

  /** 0.28 The contact file's last contact and notes, as past conversations (at most 500 rows a call). */
  importHistoryRows(rows: HistoryRow[]) {
    return this.act<{ imported: number; exists: number; skipped: number; failed: number }>("import_history_rows", { rows });
  }

  /** 2.17 How many of a search's firms can be reached, and how (worked out on 1,000 when there are more). */
  refCoverage(search: string) {
    return this.rpc<Coverage>("crm_ref_coverage", { p_tenant: this.tenantId, p_search: search });
  }

  /** 2.16 Best customers (company numbers, domains or names, up to 10) to firms in the prospect database. */
  async refLookup(inputs: string[]) {
    return (await this.rpc<LookupRow[] | null>("crm_ref_lookup_firms", { p_tenant: this.tenantId, p_inputs: inputs.slice(0, 10) })) ?? [];
  }

  /** 2.33 The business's own first emails, replies and meetings, and its mailboxes, for the cadence planner. */
  cadenceInputs() {
    return this.rpc<CadenceInputs>("crm_cadence_inputs", { p_tenant: this.tenantId });
  }

  /** 2.33 Save the cadence plan as a settings version (approved at 0.5). */
  proposeCadence(plan: Record<string, unknown>) {
    return this.act<number>("propose_cadence", { plan });
  }

  /** E18 Changes to firms this business picked. */
  refChanges(limit = 100) {
    return this.rpc<{ organisation_id: string; organisation_name: string; ref_firm_id: string; kind: string; field: string | null; old: unknown; new: unknown; detected_at: string }[]>(
      "crm_ref_changes", { p_tenant: this.tenantId, p_limit: limit },
    );
  }

  ackProspectChanges(organisation: string) {
    return this.act<void>("ack_prospect_changes", { organisation });
  }

  /** E19 A firm asked never to be contacted via Ogma: applies across every business. */
  ogmaSuppress(kind: string, value: string, reason: string) {
    return this.act<{ kind: string; value: string }>("ogma_suppress", { kind, value, reason });
  }

  // ---------------------------------------------------------------- 1.49-1.51 calls to action
  /** The business's CTAs with clicks, leads, meetings and won deals each brought in. */
  ctas(includeRetired = false) {
    return this.rpc<CtaRow[]>("crm_ctas", { p_tenant: this.tenantId, p_include_retired: includeRetired });
  }

  // ---------------------------------------------------------------- 0.30, 0.31 WhatsApp (lib/whatsapp.ts)
  whatsappAccount() {
    return this.rpc<WhatsAppAccount | null>("crm_whatsapp_account", { p_tenant: this.tenantId });
  }
  whatsappTemplates() {
    return this.rpc<WhatsAppTemplate[]>("crm_whatsapp_templates", { p_tenant: this.tenantId });
  }
  whatsappSignupConfig() {
    return this.act<SignupConfig>("whatsapp_signup_config", {});
  }
  /** After Embedded Signup: act exchanges the code server-side and returns the account and any settings version to approve. */
  connectWhatsApp(r: SignupResult) {
    return this.act<{ account: WhatsAppAccount; settings_version: number | null }>("connect_whatsapp", {
      code: r.code, waba_id: r.waba_id, phone_number_id: r.phone_number_id, coexistence: r.coexistence, ...(r.business_id ? { business_id: r.business_id } : {}),
    });
  }
  whatsappChecklist(ticks: Partial<Record<"business_verification" | "display_name" | "payment_method", boolean>>) {
    return this.act<WhatsAppAccount>("whatsapp_checklist", ticks);
  }
  refreshWhatsApp() {
    return this.act<WhatsAppAccount>("refresh_whatsapp_account", {});
  }
  decideWhatsAppTemplate(template: string, approve: boolean, body?: string, note?: string) {
    return this.act<{ status: string; submitted: boolean; error?: string; note?: string }>("decide_whatsapp_template", {
      template, approve, ...(body !== undefined ? { body } : {}), ...(note?.trim() ? { note: note.trim() } : {}),
    });
  }
  submitWhatsAppTemplate(template: string) {
    return this.act<{ meta_status: string }>("submit_whatsapp_template", { template });
  }
  syncWhatsAppTemplates() {
    return this.act<{ listed: number; updated: number }>("sync_whatsapp_templates", {});
  }

  // ---------------------------------------------------------------- 0.12 Instagram and Facebook direct (lib/meta.ts)
  /** The connected Instagram and Facebook accounts (crm_meta_accounts: never a token). */
  async metaAccounts(): Promise<MetaAccount[]> {
    return readMetaAccounts(await this.rpc<unknown>("crm_meta_accounts", { p_tenant: this.tenantId }));
  }
  /** What the browser needs to open Facebook Login for Business; refused plainly when the server isn't set up. */
  metaLoginConfig() {
    return this.act<MetaLoginConfig>("meta_login_config", {});
  }
  /** The code from Facebook Login for Business: act exchanges it, stores the tokens and records the accounts. */
  connectMeta(code: string) {
    return this.act<MetaConnectResult>("connect_meta", { code });
  }
  disconnectMeta(externalId: string) {
    return this.act<void>("disconnect_meta", { external_id: externalId });
  }
  /** A ten-minute signed link to Instagram Login (oauth-instagram /start), for an Instagram account with no Page. */
  async instagramConnectUrl(): Promise<string> {
    const r = await this.act<{ url?: string }>("instagram_connect_url", {});
    if (!r?.url) throw new CrmError("The Instagram link couldn't be made. Try again in a moment.");
    return r.url;
  }
  /** The accounts and whether this person may connect and disconnect them (an owner; members read only). */
  async metaAccountsView(): Promise<{ accounts: MetaAccount[]; canManage: boolean }> {
    return readMetaAccountsView(await this.rpc<unknown>("crm_meta_accounts", { p_tenant: this.tenantId }));
  }
  /** Back from Instagram Login (?instagram=pending&n=): act checks this person is the owner who signed in and records
   * the account. */
  finishInstagramConnect(nonce: string) {
    return this.act<{ accounts?: MetaAccount[] }>("finish_instagram_connect", { nonce });
  }
  /** 1.6 The answer to a meta_post_check task: did the post go out on Instagram or Facebook? */
  settleMetaPost(item: string, went: boolean, url: string | null = null) {
    return this.act<{ status: string }>("settle_meta_post", { item, went, ...(url ? { url } : {}) });
  }

  // ---------------------------------------------------------------- 10.13 WhatsApp updates, spend, disconnect (lib/whatsappUpdates.ts)
  whatsappUpdatePreview(audience: string, template: string | null = null) {
    return this.rpc<UpdatePreview>("crm_whatsapp_update_preview", { p_tenant: this.tenantId, p_audience: audience, p_template: template });
  }
  whatsappUpdates() {
    return this.rpc<WhatsAppUpdate[]>("crm_whatsapp_updates", { p_tenant: this.tenantId, p_limit: 50 });
  }
  whatsappSpend(month: string | null = null) {
    return this.rpc<WhatsAppSpend>("crm_whatsapp_spend", { p_tenant: this.tenantId, p_month: month });
  }
  saveWhatsAppUpdate(d: UpdateDraft) {
    return this.act<{ id: string; status: string; cta_url: string | null }>("save_whatsapp_update", { ...d });
  }
  submitWhatsAppUpdate(id: string) {
    return this.act<{ id: string; status: string; counts: Partial<UpdatePreview> }>("submit_whatsapp_update", { id });
  }
  decideWhatsAppUpdate(id: string, approve: boolean, note?: string) {
    return this.act<{ id: string; status: string; queued?: number }>("decide_whatsapp_update", { id, approve, ...(note?.trim() ? { note: note.trim() } : {}) });
  }
  /** channels.whatsapp.updates as a playbook version to approve; returns the version number. */
  proposeWhatsAppUpdateSettings(values: Partial<UpdateSettings>) {
    return this.act<number>("propose_whatsapp_update_settings", { ...values });
  }
  cancelWhatsAppUpdate(id: string) {
    return this.act<{ id: string; status: string; cancelled_messages: number }>("cancel_whatsapp_update", { id });
  }
  disconnectWhatsApp(reason?: string) {
    return this.act<{ status: string; unsubscribed: boolean; meta_error: string | null; next: string; messages_cancelled: number }>(
      "disconnect_whatsapp", { confirm: true, ...(reason?.trim() ? { reason: reason.trim() } : {}) });
  }
  whatsappSync(kind?: "contacts" | "history") {
    return this.act<{ requests: { kind: string; ok: boolean; error?: string }[] }>("whatsapp_sync", kind ? { kind } : {});
  }

  // ---------------------------------------------------------------- 6.13, 4.9 WhatsApp in Connect (lib/whatsappInbox.ts)
  /** Open windows by time left, with the replies drafted for each; and conversations whose window closed unanswered. */
  whatsappInbox() {
    return this.rpc<WaInbox>("crm_whatsapp_inbox", { p_tenant: this.tenantId });
  }
  /** One person's WhatsApp conversation, newest first, with the window. */
  whatsappThread(person: string, limit = 50) {
    return this.rpc<WaThread>("crm_whatsapp_thread", { p_tenant: this.tenantId, p_person: person, p_limit: limit });
  }
  decideWhatsAppReply(outbox: string, approve: boolean, opts: { body?: string; note?: string; reject_reason?: string } = {}) {
    return this.act<{ outbox_id: string; status: string; edited: boolean }>("decide_whatsapp_reply", {
      outbox, approve, ...(opts.body !== undefined ? { body: opts.body } : {}), ...(opts.note?.trim() ? { note: opts.note.trim() } : {}),
      ...(opts.reject_reason ? { reject_reason: opts.reject_reason } : {}),
    });
  }
  sendWhatsAppReply(person: string, body: string) {
    return this.act<{ outbox_id: string; status: string; cancelled: number }>("send_whatsapp_reply", { person, body });
  }
  /** Take a conversation over from the assistant (or give it back with back: true). */
  handOffWhatsApp(person: string, back = false, note?: string) {
    return this.act<{ handed_off: boolean; cancelled?: number }>("hand_off_whatsapp", { person, ...(back ? { back: true } : {}), ...(note?.trim() ? { note: note.trim() } : {}) });
  }
  /** channels.whatsapp settings (reply mode, quiet hours ...) as a settings version you then approve (decideConfig). */
  proposeWhatsAppSettings(values: { reply_mode?: "draft" | "sample" | "auto"; quiet_hours?: { start: string; end: string } }) {
    return this.act<number>("propose_whatsapp_settings", values);
  }
  /** The window has closed: the approved follow-up template, drafted for your approval (4.9). */
  whatsappFollowUp(person: string) {
    return this.act<{ task_id: string; queued: boolean }>("whatsapp_follow_up", { person });
  }
  /** 5.9 Send the approved opt_in_request template: a YES to it (within 7 days) opts the person in to updates. */
  requestWhatsAppOptIn(person: string) {
    return this.act<{ outbox_id: string }>("request_whatsapp_opt_in", { person });
  }
  /** 5.9 Record an opt-in the person gave you some other way, with a note of how (required). */
  recordWhatsAppOptIn(person: string, kind: "marketing" | "utility", note: string) {
    return this.act<{ changed: boolean }>("record_whatsapp_opt_in", { person, kind, note });
  }

  // ---------------------------------------------------------------- 4.13-4.15, E42, 4.5 live calls
  /** Today's call list: ranked, with each call's brief, and who was left off and why. */
  callList(date: string | null = null) {
    return this.rpc<CallList>("crm_call_list", { p_tenant: this.tenantId, p_date: date, p_limit: null });
  }

  /** 4.14 Start a call; the database checks TPS/CTPS, suppression and hours again at this moment. */
  startCall(call: string | null, person: string | null = null) {
    return this.act<StartedCall>("start_call", call ? { call } : { person });
  }

  /** 4.15 What happened, in one tap. */
  logCallOutcome(call: string, outcome: CallOutcome, opts: { notes?: string; follow_up_at?: string | null; right_person_name?: string } = {}) {
    const params: Record<string, unknown> = { call, outcome };
    if (opts.notes?.trim()) params.notes = opts.notes.trim();
    if (opts.follow_up_at) params.follow_up_at = opts.follow_up_at;
    if (opts.right_person_name?.trim()) params.right_person_name = opts.right_person_name.trim();
    return this.act<OutcomeResult>("log_call_outcome", params);
  }

  /** E42 A number checked by hand. Marking it clear needs where it was checked and the person's tick. */
  recordPhoneCheck(phone: string, tps: boolean, ctps: boolean, opts: { source?: PhoneCheckSource; confirmed?: boolean } = {}) {
    const params: Record<string, unknown> = { phone, tps, ctps };
    if (opts.source) params.source = opts.source;
    if (opts.confirmed !== undefined) params.confirmed = opts.confirmed;
    return this.act<{ status: string }>("record_phone_check", params);
  }

  /** 4.5 A name on the LinkedIn task: request sent (done) or skipped. */
  markLinkedinDone(task: string, enrolment: string, done: boolean) {
    return this.act<{ task_id: string; left: number }>("mark_linkedin_done", { task, enrolment, done });
  }

  /** The session's access token, for the calls function's /token (Twilio mode only). */
  async accessToken(): Promise<string | null> {
    const { data } = await this.sb.auth.getSession();
    return data.session?.access_token ?? null;
  }

  /** The open LinkedIn task (4.5, manual), if there is one. */
  linkedinTask() {
    return this.rpc<HumanTask | null>("crm_linkedin_task", { p_tenant: this.tenantId });
  }

  // ---------------------------------------------------------------- 1.19, 1.23 routes and audiences
  /** Who the business talks to, and where: its own audiences (attract.audiences) or the default. */
  audiences() {
    return this.rpc<AudiencesRead>("crm_audiences", { p_tenant: this.tenantId });
  }

  /** Attract › Routes: this month's work per route and stage (month: the first of a month, YYYY-MM-DD). */
  routesBoard(month: string | null = null, audience: string | null = null) {
    return this.rpc<RoutesBoard>("crm_routes_board", { p_tenant: this.tenantId, p_month: month, p_audience: audience });
  }

  /** The pieces behind one cell of Attract › Routes. */
  routePieces(route: string, stage: Exclude<StageKey, "presence">, month: string | null = null, audience: string | null = null) {
    return this.rpc<RoutePiece[]>("crm_route_pieces", { p_tenant: this.tenantId, p_route: route, p_stage: stage, p_month: month, p_audience: audience });
  }

  // ---------------------------------------------------------------- 1.52-1.55 the content library
  /** The library's segments (best hooks first), with how many pieces and cuts use each. */
  segments(status: "active" | "retired" | null = "active", role: string | null = null) {
    return this.rpc<SegmentRow[]>("crm_segments", { p_tenant: this.tenantId, p_status: status, p_role: role });
  }

  /** Published posts and clips the engine may post again (1.55), by the rules of social.evergreen. */
  evergreenCandidates(minGapDays = 90, maxReposts = 3) {
    return this.rpc<EvergreenRow[]>("crm_evergreen_candidates", { p_tenant: this.tenantId, p_min_gap_days: minGapDays, p_max_reposts: maxReposts });
  }

  /** Post experiments (1.54) with each arm's pieces and results; running ones by default. */
  contentExperiments(statuses: string[] = ["running"]) {
    return this.rpc<ContentExperiment[]>("crm_content_experiments", { p_tenant: this.tenantId, p_status: statuses });
  }

  // ---------------------------------------------------------------- 1.26-1.31 ads
  /** Ads settings, posts-before-ads readiness and every campaign with its ads, spend against the approved cap and proposals. */
  adCampaigns() {
    return this.rpc<AdsView>("crm_ad_campaigns", { p_tenant: this.tenantId, p_include_ended: false });
  }

  /** 1.28 Approve a campaign with a specific monthly cap, or reject it with the reason. */
  decideAds(campaign: string, approve: boolean, cap?: number, note?: string) {
    const n = note?.trim();
    return this.act<{ status: string; approved_cap: number | null }>("decide_ads", {
      campaign, approve, ...(approve && cap !== undefined ? { cap } : {}), ...(n ? { note: n.slice(0, 2000) } : {}),
    });
  }

  /** 1.31 -> 1.28 Approve or reject a proposed change (a cap change may approve a different cap). */
  decideAdChange(adjustment: string, approve: boolean, cap?: number) {
    return this.act<{ status: string }>("decide_ad_change", { adjustment, approve, ...(approve && cap !== undefined ? { cap } : {}) });
  }

  /** 1.29 The campaign is running on the platform (made from the upload pack); the platform's campaign id if known. */
  markAdsLive(campaign: string, platformCampaignId?: string) {
    const id = platformCampaignId?.trim();
    return this.act<{ status: string }>("mark_ads_live", { campaign, ...(id ? { platform_campaign_id: id } : {}) });
  }

  /** 1.26 Let ads start before two weeks of posts, with the reason. */
  allowAdsEarly(reason: string) {
    return this.act<string>("allow_ads_early", { reason: reason.trim().slice(0, 500) });
  }

  /** 1.30 The owner's own spend for one day, for a campaign set up by hand; the database pauses it at the cap. */
  recordAdSpend(campaign: string, day: string, spend: number, extra: { clicks?: number; leads?: number } = {}) {
    const more = Object.fromEntries(Object.entries(extra).filter(([, v]) => v !== undefined && Number.isInteger(v) && v >= 0));
    return this.act<{ month_spend: number; paused?: boolean; approved_cap: number | null }>("record_ad_spend", {
      campaign, day, spend: Math.round(spend * 100) / 100, ...more,
    });
  }

  /** 1.31 Pause (or end) a campaign; stopping spend never needs approval. */
  pauseAds(campaign: string, reason: string, end = false) {
    return this.act<{ status: string }>("pause_ads", { campaign, reason: reason.trim().slice(0, 300), ...(end ? { end: true } : {}) });
  }

  // ---------------------------------------------------------------- 0.14, 0.15, 0.17, 1.10, 1.39, 1.40 (migration 20261110130000)
  presence() {
    return this.rpc<PresenceView>("crm_presence", { p_tenant: this.tenantId });
  }

  /** 0.15 The owner's own sheet: saved and confirmed in one go. */
  saveIdentitySheet(sheet: IdentitySheet) {
    return this.act<string>("save_identity_sheet", { sheet });
  }

  confirmIdentitySheet(document: string) {
    return this.act<{ document: string; changed: boolean }>("confirm_identity_sheet", { document });
  }

  /** 0.17 Ticked checklist steps or the verification state for one platform. */
  updateAdSetup(platform: "meta" | "google", data: { steps_done?: string[]; verification?: string }) {
    return this.act<unknown>("update_ad_setup", { platform, ...data });
  }

  /** 0.17 The pasted ids as a settings change; the owner then approves it (decideConfig) in one click. */
  proposeAdIds(platform: "meta" | "google", ids: Record<string, string>) {
    return this.act<{ version: number }>("propose_ad_ids", { platform, ids });
  }

  adAudiences() {
    return this.rpc<AdAudience[]>("crm_ad_audiences", { p_tenant: this.tenantId });
  }

  retargetReadiness(days = 30) {
    return this.rpc<RetargetReadiness>("crm_retarget_readiness", { p_tenant: this.tenantId, p_days: days });
  }

  /** 1.10 The owner created the audience in Ads Manager and pastes its id. */
  recordAudienceId(audience: string, externalId: string) {
    return this.act<unknown>("record_audience_id", { audience, external_id: externalId.trim() });
  }

  videoStudio() {
    return this.rpc<VideoStudio>("crm_video_studio", { p_tenant: this.tenantId });
  }

  /** 1.39 Uploads a take to <business>/video/raw/<script id>.<ext>, where 1.41 picks it up (a retake replaces it). */
  async uploadTake(path: string, take: Blob): Promise<string> {
    if (!path.startsWith(`${this.tenantId}/video/raw/`)) throw new CrmError("A take goes in your own raw video folder.");
    // The database refuses a take over 500 MB or that is not a video (crm_record_video_take); say so before uploading.
    if (take.size > 500 * 1024 * 1024) throw new CrmError("That take is over 500 MB. Film a shorter one.");
    if (take.type && !take.type.startsWith("video/")) throw new CrmError("A take must be a video file.");
    const { error } = await this.sb.storage.from(DOCUMENTS_BUCKET).upload(path, take, { upsert: true, contentType: take.type || "video/webm" });
    if (error) throw friendlyError(error);
    return path;
  }

  recordVideoTake(item: string, path: string, seconds?: number) {
    return this.act<unknown>("record_video_take", { item, path, ...(seconds ? { seconds } : {}) });
  }

  /** 1.40 Consent to an avatar or a cloned voice, with an optional recording or form already uploaded. */
  recordMediaConsent(c: { person: string; kinds: ("avatar" | "voice_clone")[]; recordedOn?: string; evidencePath?: string; note?: string }) {
    if (!c.person.trim()) throw new CrmError("Say whose consent this is.");
    if (!c.kinds.length) throw new CrmError("Choose an avatar, a cloned voice, or both.");
    return this.act<string>("record_media_consent", {
      person: c.person.trim(),
      kinds: c.kinds,
      ...(c.recordedOn ? { recorded_on: c.recordedOn } : {}),
      ...(c.evidencePath ? { evidence_path: c.evidencePath } : {}),
      ...(c.note?.trim() ? { note: c.note.trim() } : {}),
    });
  }

  revokeMediaConsent(consent: string, note?: string) {
    return this.act<void>("revoke_media_consent", { consent, ...(note?.trim() ? { note: note.trim() } : {}) });
  }

  // ---------------------------------------------------------------- 1.56 syndication, 1.34 demand map, 1.37 visibility
  /** Ready-to-paste packs for the newsletter's issues, newest first, with the address once posted. */
  syndicationPacks(limit = 50) {
    return this.rpc<SyndicationPack[]>("crm_syndication_packs", { p_tenant: this.tenantId, p_limit: limit });
  }

  /** 1.56 The owner pasted a pack: its public https address. Ogma never posts it. */
  markSyndicationPosted(task: string, url: string) {
    return this.act<{ task_id: string; target: string; url: string; changed: boolean }>("mark_syndication_posted", { task, url: url.trim() });
  }

  /** The latest search-demand map (1.34) and AI search visibility check (1.37), each null until made. */
  searchReports() {
    return this.rpc<SearchReports>("crm_search_reports", { p_tenant: this.tenantId });
  }

  // ---------------------------------------------------------------- 0.23, 1.42-1.45 Get found
  /** The master business record every listing must match, with its checks (null before the agent drafts it). */
  businessRecord() {
    return this.rpc<BusinessRecord | null>("crm_business_record", { p_tenant: this.tenantId });
  }

  /** This business's listings in plan order, each with its claim checklist. */
  listings() {
    return this.rpc<Listing[]>("crm_listings", { p_tenant: this.tenantId });
  }

  /** Review requests sent (who, when, which site, how); never the address or any review text. */
  reviewRequests(limit = 50) {
    return this.rpc<ReviewRequestRow[]>("crm_review_requests", { p_tenant: this.tenantId, p_limit: limit });
  }

  /** 0.23 Confirm the record as read (the version guards against confirming something that changed meanwhile). */
  confirmBusinessRecord(version?: number) {
    return this.act<{ version: number; status: string }>("confirm_business_record", version ? { version } : {});
  }

  /** 1.43 Mark a listing done with its public address, skip it, or put it back in the plan. */
  updateListing(key: string, status: "claimed" | "live" | "skipped" | "planned", listingUrl?: string, notes?: string) {
    const url = listingUrl?.trim();
    const n = notes?.trim();
    return this.act<{ status: string }>("update_listing", { key, status, ...(url ? { listing_url: url } : {}), ...(n ? { notes: n.slice(0, 2000) } : {}) });
  }

  // ---------------------------------------------------------------- 1.45 trigger, 1.46, 1.57 reviews into Network
  /** Recorded reviews, newest first, with the drafted reply, its approval and any referral ask they led to. */
  reviews(limit = 50) {
    return this.rpc<ReviewRow[]>("crm_reviews", { p_tenant: this.tenantId, p_limit: limit });
  }

  /** The 10.3 referral asks queued for happy reviewers and warm replies (1.57). */
  reviewReferralAsks(limit = 50) {
    return this.rpc<ReferralAskRow[]>("crm_review_referral_asks", { p_tenant: this.tenantId, p_limit: limit });
  }

  // ---------------------------------------------------------------- 10.19 the happiness check before the review ask
  /** The happiness checks, newest first: who, when, the answer and comment, the owner's task (never the token). */
  happinessChecks(limit = 50) {
    return this.rpc<HappinessCheckRow[]>("crm_happiness_checks", { p_tenant: this.tenantId, p_limit: limit });
  }

  /** Per month: checks sent, happy, okay and unhappy answers and their shares, with review requests and reviews. */
  async happinessByMonth(months = 6): Promise<HappinessMonth[]> {
    return readHappinessMonths(await this.rpc<unknown>("crm_happiness_by_month", { p_tenant: this.tenantId, p_months: months }));
  }

  /** 1.46 Add a public review by hand (until the Google Business Profile connection reads them). */
  recordReview(r: NewReview) {
    const body = r.body?.trim();
    const url = r.review_url?.trim();
    return this.act<{ id: string; created: boolean }>("record_review", {
      site: r.site,
      rating: r.rating,
      reviewer_name: r.reviewer_name.trim(),
      ...(body ? { body: body.slice(0, 5000) } : {}),
      ...(url ? { review_url: url } : {}),
      ...(r.received_on ? { received_on: r.received_on } : {}),
    });
  }

  /** 1.46 The approved reply has been pasted under the review. */
  markReviewReplied(review: string, url?: string) {
    const u = url?.trim();
    return this.act<{ reply_status: string }>("mark_review_replied", { review, ...(u ? { url: u } : {}) });
  }

  // ---------------------------------------------------------------- 2.22-2.29 Network events
  /** The business's events not yet over (with p_include_past, the last year's too), counts and open decisions. */
  events(includePast = false) {
    return this.rpc<EventRow[]>("crm_events", { p_tenant: this.tenantId, p_include_past: includePast, p_limit: 300 });
  }

  /** One event with its pitches, meeting list and people met. */
  eventDetail(id: string) {
    return this.rpc<EventDetail | null>("crm_event_detail", { p_tenant: this.tenantId, p_event: id });
  }

  /** Per chosen event: people met, meetings, deals and cost per meeting. */
  eventResults() {
    return this.rpc<EventResult[]>("crm_event_results", { p_tenant: this.tenantId });
  }

  /** 2.22 What is coming up in the business's industry (its niches and markets in network.events), soonest first. */
  industryCalendar() {
    return this.rpc<IndustryCalendar>("crm_industry_calendar", { p_tenant: this.tenantId });
  }

  /** 2.24 Choose an event (attend, exhibit, speak, sponsor, award) or skip it. */
  decideEvent(event: string, choice: EventChoice, note?: string) {
    const n = note?.trim();
    return this.act<{ status: string; our_role: string | null }>("decide_event", { event, choice, ...(n ? { note: n.slice(0, 2000) } : {}) });
  }

  /** 2.24 Confirmed, declined or attended, and what it really cost. */
  updateEvent(event: string, change: { status?: "confirmed" | "declined" | "attended"; cost?: number; note?: string }) {
    const n = change.note?.trim();
    return this.act<{ status: string }>("update_event", {
      event,
      ...(change.status ? { status: change.status } : {}),
      ...(change.cost !== undefined ? { cost: change.cost } : {}),
      ...(n ? { note: n.slice(0, 2000) } : {}),
    });
  }

  /** 2.22 Add an event by hand. */
  addEvent(e: NewEvent) {
    const data: Record<string, unknown> = { name: e.name.trim(), starts_on: e.starts_on };
    for (const k of ["ends_on", "url", "place", "organiser", "organiser_contact", "deadline"] as const) {
      const v = e[k]?.trim();
      if (v) data[k] = v;
    }
    if (e.country?.trim()) data.country = e.country.trim().toUpperCase();
    if (e.online !== undefined) data.online = e.online;
    if (e.cost !== undefined && Number.isFinite(e.cost)) data.cost = e.cost;
    if (e.deadline?.trim() && e.deadline_kind) data.deadline_kind = e.deadline_kind;
    if (e.roles?.length) data.roles = e.roles;
    return this.act<{ id: string | null; created: boolean; skipped?: string }>("add_event", data);
  }

  /** 2.25 Approve a pitch (with any edits) or reject it with a note. */
  decideEventPitch(event: string, pitch: string, approve: boolean, edits: { subject?: string; body?: string; note?: string } = {}) {
    const note = edits.note?.trim();
    return this.act<{ status: string }>("decide_event_pitch", {
      event,
      pitch,
      approve,
      ...(note ? { note: note.slice(0, 2000) } : {}),
      ...(approve && edits.subject?.trim() ? { subject: edits.subject.trim().slice(0, 200) } : {}),
      ...(approve && edits.body?.trim() ? { body: edits.body.trim().slice(0, 5000) } : {}),
    });
  }

  /** 2.25 Withdraw a pitch that has not gone yet. */
  cancelEventPitch(event: string, pitch: string) {
    return this.act<{ status: string }>("cancel_event_pitch", { event, pitch });
  }

  /** 2.28 Someone met at an event, typed in, or someone already in the CRM. */
  captureEventContact(event: string, c: NewContact | { person: string }) {
    const data: Record<string, unknown> = { event };
    if ("person" in c) data.person = c.person;
    else
      for (const k of ["full_name", "email", "phone", "role", "company", "note"] as const) {
        const v = c[k]?.trim();
        if (v) data[k] = v;
      }
    return this.act<{ captured: boolean; person_id?: string; reason?: string }>("capture_event_contact", data);
  }

  /** 2.28 A photo of a business card or badge: stored in the business's events folder, then read by the inbox agent. */
  async addEventCard(event: string, file: File) {
    const path = await this.uploadDocument(file, "events");
    return this.act<string>("add_event_card", { event, path });
  }

  // ---------------------------------------------------------------- E3 engine switches
  /** Stations and channels this business has paused or resumed; a key not listed is on. The switches a module's Rules
   *  own (the newsletter, #513) are left out: an old pause of one is ignored, as the engine ignores it. */
  async engineSwitches() {
    const rows = await this.rpc<EngineSwitch[]>("crm_engine_switches", { p_tenant: this.tenantId });
    return (rows ?? []).filter((s) => !(s.key in RULES_SWITCHES));
  }

  /** Pause or resume a station (reach) or a channel (reach:letter). Never edits the playbook or skips a gate. */
  setEngineSwitch(key: string, paused: boolean, reason?: string) {
    const r = reason?.trim();
    return this.act<EngineSwitch>("set_engine_switch", { key, paused, ...(r ? { reason: r.slice(0, 300) } : {}) });
  }

  // ---------------------------------------------------------------- 0.24 / E3 the engine's schedule
  /** Every part of this business's engine: how often it runs, its last and next run, and recent failures. */
  engineSchedule() {
    return this.rpc<EngineSchedule>("crm_engine_schedule", { p_tenant: this.tenantId });
  }

  /** Switch a part on or off, or set how often it runs. The part becomes the owner's: defaults never change it again. */
  setEngineSchedule(agent: string, job: string, settings: Partial<CadenceSettings> & { enabled?: boolean }) {
    return this.act<EnginePart>("set_engine_schedule", { agent, job, ...settings });
  }

  /** Run a part once, now, outside its schedule. */
  runEngineNow(agent: string, job: string) {
    return this.act<{ id: string; status: string; already: boolean }>("run_engine_now", { agent, job });
  }

  /** 2.20, 4.R Run one step of one Find campaign now, in the campaign drafting part (outreach-agent draft). */
  runCampaignStepNow(campaign: string, step: CampaignRunStep) {
    return this.act<{ id: string; status: string; already: boolean }>("run_engine_now", { agent: "outreach-agent", job: "draft", campaign, step });
  }

  /** The latest Run now request for each step of one campaign, to show asked, running, done or why nothing ran. */
  campaignRunRequests(campaign: string) {
    return this.rpc<CampaignRunRequests>("crm_campaign_run_requests", { p_tenant: this.tenantId, p_campaign: campaign });
  }

  // ---------------------------------------------------------------- E15 how it works (process map and sign-off)
  /** Per step, for this business: does it apply, is it a gate, is its account connected, activity and open tasks. */
  processMap() {
    return this.rpc<StepLive[]>("crm_process_map", { p_tenant: this.tenantId });
  }

  /** The latest dev-stage sign-off per step. */
  stepSignoffs() {
    return this.rpc<StepSignoff[]>("crm_step_signoffs", { p_tenant: this.tenantId });
  }

  /** The step drawer: the last events at the step (no payloads), open tasks and the sign-off history. */
  processStepDetail(step: string, limit = 5) {
    return this.rpc<StepDetail>("crm_process_step_detail", { p_tenant: this.tenantId, p_step: step, p_limit: limit });
  }

  /** Record how a step did in dev testing. Needs changes must say what. */
  signoffStep(step: string, status: SignoffStatus, opts: { note?: string; evidence?: string; testedOn?: string } = {}) {
    const note = opts.note?.trim();
    const evidence = opts.evidence?.trim();
    const tested = status === "not_tested" ? undefined : opts.testedOn?.trim();
    return this.act<Omit<StepSignoff, "decisions">>("signoff_step", {
      step,
      status,
      ...(note ? { note: note.slice(0, 2000) } : {}),
      ...(evidence ? { evidence: evidence.slice(0, 1000) } : {}),
      ...(tested ? { tested_on: tested } : {}),
    });
  }

  /** Spend today and this month against the caps, for Claude and each paid vendor. */
  budgetStatus() {
    return this.rpc<BudgetStatus[]>("crm_budget_status", { p_tenant: this.tenantId });
  }

  touches(limit = 1000) {
    return this.rows<Record<string, unknown>>(
      this.t("touches").select("sent_at, channel, step_id, status, subject, organisation_id, person_id, external_id").eq("tenant_id", this.tenantId).order("sent_at", { ascending: false }).limit(limit),
    );
  }

  // ---------------------------------------------------------------- Ask the CRM (read-only; crm_ask_query checks the spec)
  /** Runs a spec directly, without the model: used when a chip, sort or column changes, and for exports. */
  async askQuery(spec: AskSpec): Promise<AskResult> {
    const r = await this.rpc<AskQueryResponse>("crm_ask_query", { p_tenant: this.tenantId, p_spec: spec });
    return resultFrom(spec, r);
  }

  askColumns(source: string) {
    return this.rpc<AskColumn[]>("crm_ask_columns", { p_source: source });
  }

  // ---------------------------------------------------------------- acting on an Ask list (4.11, 4.12)
  /** Who an action would include and leave out, and why. A read, run as this person (crm_preview_action). */
  async previewAction(spec: AskSpec, action: ActionKind, params: ActionParams): Promise<ActionPreview> {
    const r = await this.rpc<PreviewResponse>("crm_preview_action", { p_tenant: this.tenantId, p_spec: actionSpec(spec), p_action: action, p_params: params });
    return shapePreview(r ?? {});
  }

  /** 4.11 Confirms an email campaign. The server works the list out again and freezes it; nothing is sent until 4.10. */
  createCampaign(spec: AskSpec, params: ActionParams, name: string, brief: string) {
    if (!name.trim()) throw new CrmError("Give the campaign a name.");
    if (!brief.trim()) throw new CrmError("Say what the email is for.");
    return this.act<CampaignCreated>("create_campaign", { spec: actionSpec(spec), action: "email", params, name: name.trim(), brief: brief.trim() });
  }

  /** 4.12 Tags, enrols, creates tasks, moves firms or records an introducer for the list, row by row. */
  runBulkAction(spec: AskSpec, action: Exclude<ActionKind, "email">, params: ActionParams) {
    return this.act<BulkResult>("run_bulk_action", { spec: actionSpec(spec), action, params });
  }

  cancelCampaign(campaign: string, reason?: string) {
    return this.act<{ cancelled_messages: number }>("cancel_campaign", { campaign, ...(reason?.trim() ? { reason: reason.trim() } : {}) });
  }

  campaigns(limit = 50) {
    return this.rows<Campaign>(this.t("v_campaigns").select("*").eq("tenant_id", this.tenantId).order("created_at", { ascending: false }).limit(limit));
  }

  sequences() {
    return this.rows<{ id: string; name: string }>(this.t("sequences").select("id, name").eq("tenant_id", this.tenantId).eq("active", true).order("name"));
  }

  // ---------------------------------------------------------------- 0.7-0.25 domain, email and website (lib/domainSetup.ts)
  /** Which of Ogma's central services this server has (Google sign-in, newsletter domains) and the intake address. */
  setupInfo() {
    return this.act<SetupInfo>("setup_info", {});
  }

  /** 0.7 The latest DNS check of each domain. */
  dnsChecks() {
    return this.rpc<DnsCheck[]>("crm_dns_checks", { p_tenant: this.tenantId, p_domain: null });
  }

  /** 0.7 Check the domain's DNS live now; the result is recorded. */
  checkDns(domain: string, website: "connect" | "ogma" | "none" = "connect") {
    return this.act<DnsCheck>("check_dns", { domain, website });
  }

  /** 0.7 Who runs the domain's DNS: its nameservers, looked up live (a read; nothing is recorded). */
  dnsHost(domain: string) {
    return this.act<DnsHost>("dns_host", { domain });
  }

  /** 0.7 The business's domains and whether each is proved (the TXT record). */
  domainVerifications() {
    return this.rpc<DomainVerification[]>("crm_domain_verifications", { p_tenant: this.tenantId });
  }

  /** 0.7 The TXT record to add for a domain (made the first time it is asked for). */
  domainVerification(domain: string) {
    return this.act<DomainVerification>("domain_verification", { domain });
  }

  /** 0.7 Look the TXT record up live; the domain is proved once it is there. */
  verifyDomain(domain: string) {
    return this.act<DomainVerification>("verify_domain", { domain });
  }

  /** 0.11 The newsletter sending domain on Ogma's Resend account. */
  sendingDomains() {
    return this.rpc<SendingDomain[]>("crm_sending_domains", { p_tenant: this.tenantId });
  }

  createSendingDomain(domain: string) {
    return this.act<SendingDomain>("create_sending_domain", { domain });
  }

  checkSendingDomain() {
    return this.act<SendingDomain>("check_sending_domain", {});
  }

  /** 0.9 and 0.25 The business's sites, each with its latest install check. */
  sites() {
    return this.rpc<SiteRow[]>("crm_sites", { p_tenant: this.tenantId });
  }

  registerSite(origins: string[], siteKey?: string) {
    return this.act<{ site_key: string; origins: string[]; new: boolean }>("register_site", { origins, ...(siteKey ? { site_key: siteKey } : {}) });
  }

  checkSiteInstall(siteKey: string, contactUrl?: string) {
    return this.act<{ ok: boolean; pages: PageCheck[] }>("check_site_install", { site_key: siteKey, ...(contactUrl ? { contact_url: contactUrl } : {}) });
  }

  /** Where articles are published, and the newsletter's sender: each a settings version, approved with decideConfig. */
  proposePublishing(params: JsonObject) {
    return this.act<{ version: number }>("propose_publishing", params);
  }

  proposeNewsletterSender(fromAddress: string, replyTo?: string) {
    return this.act<{ version: number }>("propose_newsletter_sender", { from_address: fromAddress, ...(replyTo ? { reply_to: replyTo } : {}) });
  }

  // ---------------------------------------------------------------- 0.13 brand capture, 0.18-0.22 and 1.32-1.33 the website studio
  /** The latest brand capture and the asset pool's counts. */
  brandState() {
    return this.rpc<BrandState>("crm_brand_state", { p_tenant: this.tenantId });
  }

  /** The asset pool; with usableOnly, only assets cleared for public use (rights confirmed, consent for people). */
  brandAssets(usableOnly = false) {
    return this.rpc<BrandAsset[]>("crm_brand_assets", { p_tenant: this.tenantId, p_kind: null, p_subject: null, p_usable_only: usableOnly });
  }

  /** Uploads one brand file to <tenant_id>/brand/uploads/ (the storage rule keeps a member in their own folder). */
  uploadBrandFile(file: File) {
    // The same limits as the database and the engine: an image (SVG, PNG, JPG, WebP) up to 10 MB, a PDF or deck up to 20 MB.
    const name = file.name.toLowerCase();
    const doc = /\.(pdf|pptx)$/.test(name);
    if (!/\.(svg|png|jpe?g|webp|pdf|pptx)$/.test(name)) return Promise.reject(new CrmError("Upload an SVG, PNG, JPG, WebP, PDF or PPTX file."));
    if (file.size > (doc ? 20 : 10) * 1024 * 1024) return Promise.reject(new CrmError(`${file.name} is over ${doc ? 20 : 10} MB.`));
    return this.uploadDocument(file, BRAND_FOLDER);
  }

  requestBrandCapture(p: { website?: string; line?: string; uploads?: string[]; changes?: string; rejectVersion?: number }) {
    return this.act<{ capture: string; task: string }>("request_brand_capture", {
      ...(p.website?.trim() ? { website: p.website.trim() } : {}),
      ...(p.line?.trim() ? { line: p.line.trim() } : {}),
      ...(p.uploads?.length ? { uploads: p.uploads } : {}),
      ...(p.changes?.trim() ? { changes: p.changes.trim() } : {}),
      ...(p.rejectVersion ? { reject_version: p.rejectVersion } : {}),
    });
  }

  tagBrandAsset(asset: string, change: { kind?: string; subjects?: string[]; alt?: string; shows_people?: boolean }) {
    return this.act<void>("tag_brand_asset", { asset, ...change });
  }

  retireBrandAsset(asset: string) {
    return this.act<void>("retire_brand_asset", { asset });
  }

  confirmBrandRights(peopleConsent: boolean) {
    return this.act<number>("confirm_brand_rights", { people_consent: peopleConsent });
  }

  /** The site: its state, every version (without documents) and Ogma's open requests. */
  site() {
    return this.rpc<SiteState>("crm_site", { p_tenant: this.tenantId });
  }

  siteVersion(version: number) {
    return this.rpc<SiteVersion | null>("crm_site_version", { p_tenant: this.tenantId, p_version: version });
  }

  saveSiteBrief(brief: SiteBrief) {
    return this.act<{ site_id: string; document_id: string }>("save_site_brief", { brief });
  }

  requestSiteDraft(importUrl?: string, note?: string) {
    return this.act<string>("request_site_draft", { ...(importUrl?.trim() ? { import_url: importUrl.trim() } : {}), ...(note?.trim() ? { note: note.trim() } : {}) });
  }

  /** 1.32 One click edit, saved as a new version. */
  updateSiteSection(p: { version: number; page: string; op: "update" | "insert" | "remove" | "move"; index: number; section?: { type?: string; fields: Record<string, unknown> }; to?: number }) {
    return this.act<{ version: number; placeholders: number }>("update_site_section", {
      version: p.version, page: p.page, op: p.op, index: p.index, ...(p.section ? { section: p.section } : {}), ...(p.to !== undefined ? { to: p.to } : {}),
    });
  }

  requestSiteRewrite(version: number, page: string, section: string, instruction: string) {
    return this.act<string>("request_site_rewrite", { version, page, section, instruction });
  }

  approveSiteVersion(version: number, approve: boolean, note?: string) {
    return this.act<void>("approve_site_version", { version, approve, ...(note?.trim() ? { note: note.trim() } : {}) });
  }

  publishSite(version: number) {
    return this.act<{ version: number; task: string }>("publish_site", { version });
  }

  restoreSiteVersion(version: number) {
    return this.act<{ version: number; task: string }>("restore_site_version", { version });
  }

  connectDomain(domain: string) {
    return this.act<string>("connect_domain", { domain });
  }

  // ---------------------------------------------------------------- home, meetings, settings
  overview() {
    return this.rpc<Overview>("crm_overview", { p_tenant: this.tenantId });
  }

  meetings(fromIso: string, toIso: string) {
    return this.rpc<Meeting[]>("crm_meetings", { p_tenant: this.tenantId, p_from: fromIso, p_to: toIso });
  }

  // ---------------------------------------------------------------- 7.8-7.12 booking (migration 20261130090000)
  /** Meetings booked with their type and where they were booked (the booking page, Calendly, Cal.com, Ogma). */
  meetingsBooked(fromIso: string, toIso: string) {
    return this.rpc<Meeting[]>("crm_meetings_booked", { p_tenant: this.tenantId, p_from: fromIso, p_to: toIso });
  }

  /** Settings › Meetings › Booking links: the switches, the meeting types (cleaned), each type's free times now, and bookings in 30 days. */
  bookingOverview() {
    return this.rpc<BookingOverview>("crm_booking_overview", { p_tenant: this.tenantId });
  }

  /** 7.12 Move or cancel a booked meeting; the calendar event and any Zoom meeting follow. */
  changeMeeting(meeting: string, action: "move" | "cancel", change: { startsAt?: string; endsAt?: string; reason?: string } = {}) {
    const reason = change.reason?.trim() ?? "";
    if (reason.length > 300) throw new CrmError("Keep the reason under 300 characters.");
    if (action === "move" && !change.startsAt) throw new CrmError("Choose the new time.");
    return this.act<{ changed: boolean; status?: string; note?: string; reason?: string }>("change_meeting", {
      meeting, action, ...(change.startsAt ? { starts_at: change.startsAt } : {}), ...(change.endsAt ? { ends_at: change.endsAt } : {}),
      ...(reason ? { reason } : {}),
    });
  }

  tenant() {
    return this.one<TenantRow>(this.t("tenants").select("id, slug, name, config, created_at").eq("id", this.tenantId).maybeSingle());
  }

  members() {
    return this.rows<Membership>(this.t("tenant_members").select("*").eq("tenant_id", this.tenantId).order("created_at"));
  }

  // ---------------------------------------------------------------- 0.44 Start again (lib/startAgain.ts)
  /** What starting again would delete and keep (crm_reset_preview). */
  async resetPreview(): Promise<ResetPreview> {
    return readResetPreview(await this.rpc<unknown>("crm_reset_preview", { p_tenant: this.tenantId }));
  }
  /** Wipes the business and sends it back to guided set-up (act start_again; the owner only, with the name typed). */
  async startAgain(confirmName: string): Promise<ResetResult> {
    return readResetResult(await this.act<unknown>("start_again", { confirm_name: confirmName }));
  }
}

// ---------------------------------------------------------------------------- set-up polish (12 Oct 2026)
export type FirstValueState = "waiting" | "running" | "done" | "failed";
export interface FirstValueStatus {
  status: FirstValueState;
  error_text: string | null;
  queued_at: string | null;
}

/** crm_first_value_status, read tolerantly: anything unknown is "waiting". */
export function readFirstValueStatus(v: unknown): FirstValueStatus {
  const o = v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const status = (["waiting", "running", "done", "failed"] as const).find((s) => s === o.status) ?? "waiting";
  return {
    status,
    error_text: typeof o.error_text === "string" && o.error_text ? o.error_text : null,
    queued_at: typeof o.queued_at === "string" && o.queued_at ? o.queued_at : null,
  };
}

export interface HelpRequest {
  task_id: string;
  topic: string;
  detail: string | null;
  status: "open" | "done" | "cancelled";
  created_at: string;
  completed_at: string | null;
}

export function readHelpRequest(v: unknown): HelpRequest | null {
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  if (typeof o.task_id !== "string") return null;
  const status = o.status === "done" ? "done" : o.status === "cancelled" ? "cancelled" : "open";
  return {
    task_id: o.task_id,
    topic: typeof o.topic === "string" ? o.topic : "",
    detail: typeof o.detail === "string" && o.detail ? o.detail : null,
    status,
    created_at: typeof o.created_at === "string" ? o.created_at : "",
    completed_at: typeof o.completed_at === "string" ? o.completed_at : null,
  };
}

/** A help topic's key as crm_request_help wants it (^[a-z][a-z0-9_.-]{1,40}$), from the words on screen. */
export function helpTopicKey(label: string): string {
  let k = label.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "_").replace(/^[^a-z]+/, "").replace(/_+$/, "");
  if (k.length > 41) {
    // Cut at a word, not in the middle of one.
    const cut = k.slice(0, 42);
    k = cut.lastIndexOf("_") > 10 ? cut.slice(0, cut.lastIndexOf("_")) : k.slice(0, 41);
  }
  k = k.replace(/_+$/, "");
  return k.length >= 2 ? k : "help";
}

/** A help topic key in words again, for the owner's list: setting_up_email_outreach -> "Setting up email outreach". */
export function helpTopicWords(key: string): string {
  const w = key.replace(/[_.-]+/g, " ").trim();
  return w ? w.charAt(0).toUpperCase() + w.slice(1) : "Help";
}

/** crm_happiness_checks: one 10.19 check (never its token). */
export interface HappinessCheckRow {
  id: string;
  deal_id: string;
  organisation_id: string | null;
  organisation_name: string | null;
  person_id: string | null;
  person_name: string | null;
  status: "scheduled" | "queued" | "skipped";
  channel: "email" | "whatsapp" | null;
  skip_reason: string | null;
  due_at: string;
  queued_at: string | null;
  message_status: string | null;
  sent_at: string | null;
  answer: "happy" | "okay" | "unhappy" | null;
  comment: string | null;
  answered_at: string | null;
  task_id: string | null;
  task_status: string | null;
  created_at: string;
  referral_ask: { task_id: string; status: string; due_at: string } | null;
}
